import React, { useState, useEffect, createContext, useContext } from 'react';
import { Star, Play, Trophy, Camera, Users, Globe, ChevronRight, Check, Menu, X, Instagram, Youtube, Twitter, ArrowRight, Award, TrendingUp, Video, Calendar, User, MapPin, Ruler, Scale, Footprints, BookOpen, Dumbbell, Brain, Apple, Target, Lock, PlayCircle, MessageCircle, Mail, Phone, Send, Loader2, Upload, FileText, Eye, CheckCircle, Clock, AlertCircle, Languages } from 'lucide-react';
import { translations } from './translations';
import { publicApi, contactApi, playersApi, subscriptionsApi, authApi, clearTokens, isLoggedIn } from './api/client';
import { usePublicData, useAuth, usePlans } from './api/hooks';
import AdminDashboard from './AdminDashboard';



// Language Context
const LanguageContext = createContext();

const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

// Language Selector Component
const LanguageSelector = ({ scrolled }) => {
  const { language, setLanguage } = useLanguage();

  return (
    <button
      onClick={() => setLanguage(language === 'es' ? 'en' : 'es')}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border transition-all text-sm font-medium ${
        scrolled
          ? 'border-gray-300 text-gray-600 hover:border-emerald-500 hover:text-emerald-600'
          : 'border-white/30 text-white/80 hover:border-white hover:text-white'
      }`}
    >
      <Languages className="w-4 h-4" />
      {language === 'es' ? 'EN' : 'ES'}
    </button>
  );
};

// WhatsApp Floating Button Component
const WhatsAppButton = () => (
  <a
    href="https://wa.me/17869564198?text=Hola%2C%20quiero%20información%20sobre%20ProKids%20Talent"
    target="_blank"
    rel="noopener noreferrer"
    className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-green-500 hover:bg-green-600 rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all hover:scale-110"
    aria-label="Contactar por WhatsApp"
  >
    <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  </a>
);

// Sample player data for the demo profile
const samplePlayer = {
  name: "Santiago Mendoza",
  age: 9,
  position: "Mediocampista",
  location: "Ciudad de México, México",
  height: "1.38m",
  weight: "32kg",
  dominantFoot: "Derecho",
  since: "Marzo 2024",
  bio: "Santiago comenzó a jugar fútbol a los 4 años en el parque de su colonia. Su visión de juego y capacidad de pase lo destacan entre sus compañeros. Sueña con jugar en Europa y representar a la selección mexicana.",
  stats: {
    sessions: 14,
    videos: 42,
    views: "12.4K",
    followers: 847
  },
  skills: [
    { name: "Control de balón", level: 85 },
    { name: "Pase corto", level: 90 },
    { name: "Visión de juego", level: 88 },
    { name: "Velocidad", level: 75 },
    { name: "Disparo", level: 70 }
  ],
  videos: [
    { title: "Mejores jugadas - Enero 2025", views: "2.3K", thumbnail: "🎬" },
    { title: "Entrenamiento técnico #12", views: "1.8K", thumbnail: "⚽" },
    { title: "Partidillo destacado", views: "3.1K", thumbnail: "🏆" }
  ],
  achievements: [
    "MVP Torneo Interescolar 2024",
    "Mejor asistidor de su categoría",
    "14 sesiones ProKids completadas"
  ]
};

// Navigation Component
const Navigation = ({ currentPage, setCurrentPage, scrolled }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { t } = useLanguage();

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-lg' : 'bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 sm:h-20">
          {/* Logo */}
          <div
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => setCurrentPage('home')}
          >
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-xl flex items-center justify-center shadow-lg">
              <Trophy className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div>
              <span className={`text-lg sm:text-xl font-bold ${scrolled ? 'text-gray-900' : 'text-white'}`}>
                ProKids
              </span>
              <span className={`text-lg sm:text-xl font-light ${scrolled ? 'text-emerald-600' : 'text-emerald-400'}`}>
                {' '}Talent
              </span>
            </div>
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-6">
            <button
              onClick={() => setCurrentPage('home')}
              className={`font-medium transition-colors ${scrolled ? 'text-gray-600 hover:text-emerald-600' : 'text-white/80 hover:text-white'}`}
            >
              {t.nav.home}
            </button>
            <button
              onClick={() => document.getElementById('como-funciona')?.scrollIntoView({ behavior: 'smooth' })}
              className={`font-medium transition-colors ${scrolled ? 'text-gray-600 hover:text-emerald-600' : 'text-white/80 hover:text-white'}`}
            >
              {t.nav.howItWorks}
            </button>
            <button
              onClick={() => document.getElementById('elearning')?.scrollIntoView({ behavior: 'smooth' })}
              className={`font-medium transition-colors ${scrolled ? 'text-gray-600 hover:text-emerald-600' : 'text-white/80 hover:text-white'}`}
            >
              {t.nav.eLearning}
            </button>
            <button
              onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
              className={`font-medium transition-colors ${scrolled ? 'text-gray-600 hover:text-emerald-600' : 'text-white/80 hover:text-white'}`}
            >
              {t.nav.plans}
            </button>
            <button
              onClick={() => setCurrentPage('alto-rendimiento')}
              className={`font-medium transition-colors ${scrolled ? 'text-emerald-600 font-semibold' : 'text-emerald-400 font-semibold'}`}
            >
              {t.nav?.altoRendimiento || 'Alto Rendimiento'}
            </button>
            <button
              onClick={() => setCurrentPage('academy')}
              className="font-bold text-sm px-3.5 py-1.5 rounded-full bg-gradient-to-r from-emerald-500 to-lime-500 text-gray-900 transition-all hover:shadow-lg hover:shadow-lime-500/30 hover:scale-105"
            >
              {t.nav?.academy || 'Academy'}
            </button>
            <button
              onClick={() => setCurrentPage('register')}
              className={`font-medium transition-colors ${scrolled ? 'text-gray-600 hover:text-emerald-600' : 'text-white/80 hover:text-white'}`}
            >
              {t.nav.registerPlayer}
            </button>
            <LanguageSelector scrolled={scrolled} />
            <button
              onClick={() => setCurrentPage('contact')}
              className="bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-2.5 rounded-full font-semibold transition-all hover:shadow-lg hover:shadow-emerald-500/30"
            >
              {t.nav.enrollMyChild}
            </button>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-2">
            <LanguageSelector scrolled={scrolled} />
            <button
              className="p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <X className={`w-6 h-6 ${scrolled ? 'text-gray-900' : 'text-white'}`} />
              ) : (
                <Menu className={`w-6 h-6 ${scrolled ? 'text-gray-900' : 'text-white'}`} />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white rounded-2xl shadow-xl mt-2 p-4 absolute left-4 right-4">
            <div className="flex flex-col gap-2">
              <button
                onClick={() => { setCurrentPage('home'); setMobileMenuOpen(false); }}
                className="text-left px-4 py-3 rounded-xl hover:bg-gray-50 font-medium text-gray-700"
              >
                {t.nav.home}
              </button>
              <button
                onClick={() => { document.getElementById('elearning')?.scrollIntoView({ behavior: 'smooth' }); setMobileMenuOpen(false); }}
                className="text-left px-4 py-3 rounded-xl hover:bg-gray-50 font-medium text-gray-700"
              >
                {t.nav.eLearning}
              </button>
              <button
                onClick={() => { document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' }); setMobileMenuOpen(false); }}
                className="text-left px-4 py-3 rounded-xl hover:bg-gray-50 font-medium text-gray-700"
              >
                {t.nav.plans}
              </button>
              <button
                onClick={() => { setCurrentPage('alto-rendimiento'); setMobileMenuOpen(false); }}
                className="text-left px-4 py-3 rounded-xl hover:bg-emerald-50 font-semibold text-emerald-600 border border-emerald-200 bg-emerald-50/50"
              >
                🔥 {t.nav?.altoRendimiento || 'Alto Rendimiento'}
              </button>
              <button
                onClick={() => { setCurrentPage('academy'); setMobileMenuOpen(false); }}
                className="text-left px-4 py-3 rounded-xl font-bold text-gray-900 bg-gradient-to-r from-emerald-500 to-lime-500"
              >
                ⚽ {t.nav?.academy || 'Academy'}
              </button>
              <button
                onClick={() => { setCurrentPage('register'); setMobileMenuOpen(false); }}
                className="text-left px-4 py-3 rounded-xl hover:bg-gray-50 font-medium text-gray-700"
              >
                {t.nav.registerPlayer}
              </button>
              <button
                onClick={() => { setCurrentPage('contact'); setMobileMenuOpen(false); }}
                className="mt-2 bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-3 rounded-xl font-semibold transition-all"
              >
                {t.nav.enrollMyChild}
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section with typewriter effect for age
const HeroSection = ({ setCurrentPage }) => {
  const { t } = useLanguage();
  const { metrics } = usePublicData();
  const [displayText, setDisplayText] = useState('5');
  const [cursorVisible, setCursorVisible] = useState(true);
  const ages = ['5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15', '16', '17', '18'];
  const ageIndexRef = React.useRef(0);

  useEffect(() => {
    // Cursor blink effect
    const cursorInterval = setInterval(() => {
      setCursorVisible(prev => !prev);
    }, 530);

    return () => clearInterval(cursorInterval);
  }, []);

  useEffect(() => {
    const animateText = () => {
      const currentAge = ages[ageIndexRef.current];

      // Borrar caracter por caracter (lento)
      let charIndex = currentAge.length;
      const eraseInterval = setInterval(() => {
        charIndex--;
        if (charIndex >= 0) {
          setDisplayText(currentAge.substring(0, charIndex));
        } else {
          clearInterval(eraseInterval);

          // Pausa antes de escribir el siguiente
          setTimeout(() => {
            ageIndexRef.current = (ageIndexRef.current + 1) % ages.length;
            setDisplayText(ages[ageIndexRef.current]);
          }, 300);
        }
      }, 150);
    };

    // Esperar 2 segundos antes de empezar a borrar
    const mainInterval = setInterval(animateText, 2500);

    return () => clearInterval(mainInterval);
  }, []);

  // Formatear métricas dinámicas
  const statsDisplay = [
    { number: `${metrics.activeKids.toLocaleString()}+`, label: t.hero.stats.activeKids },
    { number: metrics.countries.toString(), label: t.hero.stats.countries },
    { number: `${metrics.videosCreated.toLocaleString()}+`, label: t.hero.stats.videosCreated },
    { number: metrics.connectedScouts.toString(), label: t.hero.stats.connectedScouts },
  ];

  return (
  <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
    {/* Background */}
    <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-emerald-900 to-gray-900">
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-500 rounded-full filter blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-yellow-500 rounded-full filter blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
      </div>
      {/* Soccer field pattern overlay */}
      <div className="absolute inset-0 opacity-5">
        <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
          <line x1="50" y1="0" x2="50" y2="100" stroke="white" strokeWidth="0.5"/>
          <circle cx="50" cy="50" r="15" fill="none" stroke="white" strokeWidth="0.5"/>
        </svg>
      </div>
    </div>

    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-32">
      <div className="text-center">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-6 sm:mb-8">
          <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
          <span className="text-white/90 text-sm font-medium">{t.hero.badge}</span>
        </div>

        {/* Main Headline with typewriter age effect */}
        <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
          {t.hero.title1}
          <br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-yellow-400">
            {t.hero.title2}
          </span>{' '}
          <span className="inline-block text-yellow-400" style={{ minWidth: '2ch' }}>
            {displayText}
            <span
              className={`inline-block w-[3px] sm:w-[4px] h-[0.9em] bg-yellow-400 ml-1 align-middle ${
                cursorVisible ? 'opacity-100' : 'opacity-0'
              }`}
              style={{ transition: 'opacity 0.1s' }}
            ></span>
          </span>{' '}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-yellow-400">
            {t.hero.title3}
          </span>
        </h1>

        <p className="text-lg sm:text-xl text-white/70 max-w-3xl mx-auto mb-8 sm:mb-10 leading-relaxed px-4">
          {t.hero.subtitle}
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12 sm:mb-16 px-4">
          <button
            onClick={() => setCurrentPage('register')}
            className="w-full sm:w-auto bg-emerald-500 hover:bg-emerald-400 text-white px-8 py-4 rounded-full font-bold text-lg transition-all hover:shadow-xl hover:shadow-emerald-500/30 flex items-center justify-center gap-2 group"
          >
            {t.hero.cta1}
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>
          <a href="https://wa.me/17869564198?text=Hola%2C%20quiero%20información%20sobre%20ProKids%20Talent" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/30 text-white px-8 py-4 rounded-full font-semibold text-lg transition-all flex items-center justify-center gap-2">
            <Play className="w-5 h-5" />
            {t.hero.cta2}
          </a>
        </div>

        {/* Stats — dinámicos desde el backend */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8 max-w-4xl mx-auto px-4">
          {statsDisplay.map((stat, index) => (
            <div key={index} className="text-center">
              <div className="text-2xl sm:text-4xl font-bold text-white mb-1">{stat.number}</div>
              <div className="text-white/60 text-sm sm:text-base">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* Scroll indicator */}
    <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
      <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
        <div className="w-1.5 h-3 bg-white/50 rounded-full"></div>
      </div>
    </div>
  </section>
  );
};

// Three Pillars Section

const PillarsSection = () => {
  const { t } = useLanguage();
  const pillars = [
    {
      icon: <Trophy className="w-8 h-8" />,
      title: t.pillars.items[0].title,
      subtitle: t.pillars.items[0].subtitle,
      description: t.pillars.items[0].description,
      color: "emerald",
      features: t.pillars.items[0].features
    },
    {
      icon: <Camera className="w-8 h-8" />,
      title: t.pillars.items[1].title,
      subtitle: t.pillars.items[1].subtitle,
      description: t.pillars.items[1].description,
      color: "blue",
      features: t.pillars.items[1].features
    },
    {
      icon: <Globe className="w-8 h-8" />,
      title: t.pillars.items[2].title,
      subtitle: t.pillars.items[2].subtitle,
      description: t.pillars.items[2].description,
      color: "yellow",
      features: t.pillars.items[2].features
    }
  ];

  const colorClasses = {
    emerald: {
      bg: "bg-emerald-500",
      light: "bg-emerald-50",
      text: "text-emerald-600",
      border: "border-emerald-200"
    },
    blue: {
      bg: "bg-blue-500",
      light: "bg-blue-50",
      text: "text-blue-600",
      border: "border-blue-200"
    },
    yellow: {
      bg: "bg-yellow-500",
      light: "bg-yellow-50",
      text: "text-yellow-600",
      border: "border-yellow-200"
    }
  };

  return (
    <section id="como-funciona" className="py-16 sm:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 rounded-full px-4 py-2 mb-4">
            <Star className="w-4 h-4" />
            <span className="text-sm font-semibold">{t.pillars.badge}</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t.pillars.title}
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
            {t.pillars.subtitle}
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 sm:gap-8">
          {pillars.map((pillar, index) => {
            const colors = colorClasses[pillar.color];
            return (
              <div
                key={index}
                className={`relative group bg-white rounded-3xl p-6 sm:p-8 border-2 ${colors.border} hover:border-transparent hover:shadow-2xl transition-all duration-300`}
              >
                <div className={`w-16 h-16 ${colors.bg} rounded-2xl flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform`}>
                  {pillar.icon}
                </div>
                <div className={`text-sm font-semibold ${colors.text} mb-2`}>{pillar.subtitle}</div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3">{pillar.title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{pillar.description}</p>
                <ul className="space-y-3">
                  {pillar.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full ${colors.light} flex items-center justify-center`}>
                        <Check className={`w-3 h-3 ${colors.text}`} />
                      </div>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// What's Included Section
const IncludedSection = () => {
  const included = [
    { icon: <Calendar className="w-6 h-6" />, title: "1 jornada profesional mensual", desc: "Con entrenador y equipo audiovisual" },
    { icon: <Video className="w-6 h-6" />, title: "Videos de alta calidad", desc: "Editados profesionalmente cada mes" },
    { icon: <Instagram className="w-6 h-6" />, title: "Publicación semanal", desc: "En el canal de ProKids Talent" },
    { icon: <Globe className="w-6 h-6" />, title: "Perfil web público", desc: "Indexado en Google para scouts" },
    { icon: <Users className="w-6 h-6" />, title: "Ecosistema de la agencia", desc: "Comunidad de talentos y familias" },
    { icon: <Award className="w-6 h-6" />, title: "Representación incluida", desc: "Para marcas y oportunidades deportivas" }
  ];

  return (
    <section className="py-16 sm:py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Todo esto incluido cada mes
          </h2>
          <p className="text-lg sm:text-xl text-gray-600">
            Una suscripción, acceso completo al sistema
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {included.map((item, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl p-6 flex items-start gap-4 hover:shadow-lg transition-shadow"
            >
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center text-emerald-600 flex-shrink-0">
                {item.icon}
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Bonus: Use your own content */}
        <div className="mt-8 sm:mt-12 bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-3xl p-6 sm:p-8 text-white text-center">
          <div className="text-2xl sm:text-3xl mb-2">🎁</div>
          <h3 className="text-xl sm:text-2xl font-bold mb-2">Bonus: Tu contenido, tus redes</h3>
          <p className="text-emerald-100 max-w-2xl mx-auto">
            Todo el material generado también puede ser usado en las redes personales del jugador.
            Construye su audiencia desde el primer día.
          </p>
        </div>
      </div>
    </section>
  );
};

// E-Learning Section
const ELearningSection = () => {
  const categories = [
    {
      icon: <Target className="w-8 h-8" />,
      title: "Técnica Individual",
      description: "Control de balón, pases, regates y finalizaciones",
      videos: 45,
      color: "emerald"
    },
    {
      icon: <Dumbbell className="w-8 h-8" />,
      title: "Pliometría y Físico",
      description: "Ejercicios de potencia, velocidad y coordinación",
      videos: 32,
      color: "blue"
    },
    {
      icon: <Trophy className="w-8 h-8" />,
      title: "Jugadas Tácticas",
      description: "Movimientos de equipo, posicionamiento y estrategia",
      videos: 28,
      color: "purple"
    },
    {
      icon: <Brain className="w-8 h-8" />,
      title: "Mentalidad Deportiva",
      description: "Concentración, manejo de presión y motivación",
      videos: 20,
      color: "orange"
    },
    {
      icon: <Apple className="w-8 h-8" />,
      title: "Nutrición Deportiva",
      description: "Alimentación para jóvenes atletas de alto rendimiento",
      videos: 15,
      color: "red"
    }
  ];

  const colorClasses = {
    emerald: { bg: "bg-emerald-500", light: "bg-emerald-50", text: "text-emerald-600" },
    blue: { bg: "bg-blue-500", light: "bg-blue-50", text: "text-blue-600" },
    purple: { bg: "bg-purple-500", light: "bg-purple-50", text: "text-purple-600" },
    orange: { bg: "bg-orange-500", light: "bg-orange-50", text: "text-orange-600" },
    red: { bg: "bg-red-500", light: "bg-red-50", text: "text-red-600" }
  };

  const featuredVideos = [
    { title: "Regate la croqueta - Nivel avanzado", duration: "8:45", category: "Técnica", thumbnail: "⚽" },
    { title: "Rutina pliométrica pre-partido", duration: "12:30", category: "Físico", thumbnail: "💪" },
    { title: "Cómo jugar de mediocampista", duration: "15:20", category: "Táctica", thumbnail: "🎯" }
  ];

  return (
    <section id="elearning" className="py-16 sm:py-24 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 bg-emerald-500/20 text-emerald-400 rounded-full px-4 py-2 mb-4">
            <BookOpen className="w-4 h-4" />
            <span className="text-sm font-semibold">ACADEMIA VIRTUAL</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
            E-Learning ProKids
          </h2>
          <p className="text-lg sm:text-xl text-gray-400 max-w-2xl mx-auto">
            +140 videos de entrenamiento profesional. Aprende las mejores técnicas desde casa, a tu ritmo.
          </p>
        </div>

        {/* Categories Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-12">
          {categories.map((category, index) => {
            const colors = colorClasses[category.color];
            return (
              <div
                key={index}
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5 hover:bg-white/10 transition-all cursor-pointer group"
              >
                <div className={`w-14 h-14 ${colors.bg} rounded-xl flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-transform`}>
                  {category.icon}
                </div>
                <h3 className="text-white font-bold mb-1">{category.title}</h3>
                <p className="text-gray-400 text-sm mb-3">{category.description}</p>
                <div className="flex items-center gap-2 text-emerald-400 text-sm font-medium">
                  <PlayCircle className="w-4 h-4" />
                  {category.videos} videos
                </div>
              </div>
            );
          })}
        </div>

        {/* Featured Videos */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-6 sm:p-8">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-white flex items-center gap-2">
              <Play className="w-5 h-5 text-emerald-400" />
              Videos Destacados
            </h3>
            <span className="text-emerald-400 text-sm font-medium">Ver todos →</span>
          </div>

          <div className="grid sm:grid-cols-3 gap-4">
            {featuredVideos.map((video, index) => (
              <div key={index} className="group cursor-pointer">
                <div className="aspect-video bg-gradient-to-br from-gray-700 to-gray-800 rounded-xl flex items-center justify-center text-5xl mb-3 relative overflow-hidden">
                  {video.thumbnail}
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-14 h-14 bg-emerald-500 rounded-full flex items-center justify-center">
                      <Play className="w-6 h-6 text-white ml-1" />
                    </div>
                  </div>
                  <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                    {video.duration}
                  </div>
                </div>
                <h4 className="text-white font-medium text-sm mb-1">{video.title}</h4>
                <span className="text-gray-500 text-xs">{video.category}</span>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-10">
          <p className="text-gray-400 mb-4">Acceso completo a la academia incluido en todos los planes</p>
          <button
            onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
            className="bg-emerald-500 hover:bg-emerald-400 text-white px-8 py-3 rounded-full font-semibold transition-all hover:shadow-lg hover:shadow-emerald-500/30"
          >
            Ver planes y precios
          </button>
        </div>
      </div>
    </section>
  );
};

// Pricing Section
const PricingSection = () => {
  const [billingCycle, setBillingCycle] = useState('monthly');

  const plans = [
    {
      name: "Starter",
      description: "Ideal para comenzar",
      monthlyPrice: 49,
      yearlyPrice: 39,
      color: "gray",
      popular: false,
      features: [
        { text: "Acceso completo al E-Learning", included: true },
        { text: "+140 videos de entrenamiento", included: true },
        { text: "Comunidad de padres y jugadores", included: true },
        { text: "Nuevos videos cada semana", included: true },
        { text: "Jornada mensual presencial", included: false },
        { text: "Videos personales del jugador", included: false },
        { text: "Perfil web público", included: false },
        { text: "Representación deportiva", included: false }
      ],
      cta: "Comenzar ahora"
    },
    {
      name: "Pro",
      description: "El más popular",
      monthlyPrice: 99,
      yearlyPrice: 79,
      color: "emerald",
      popular: true,
      features: [
        { text: "Todo lo de Starter +", included: true },
        { text: "1 jornada presencial mensual", included: true },
        { text: "Videos HD personalizados", included: true },
        { text: "Publicación semanal en redes", included: true },
        { text: "Perfil web público indexado", included: true },
        { text: "Uso libre del contenido", included: true },
        { text: "Soporte prioritario WhatsApp", included: true },
        { text: "Representación activa con scouts", included: false }
      ],
      cta: "Elegir Pro"
    },
    {
      name: "Elite",
      description: "Máxima proyección",
      monthlyPrice: 149,
      yearlyPrice: 119,
      color: "yellow",
      popular: false,
      features: [
        { text: "Todo lo de Pro +", included: true },
        { text: "Representación activa", included: true },
        { text: "Conexión directa con scouts", included: true },
        { text: "Prioridad en oportunidades", included: true },
        { text: "Videos premium exclusivos", included: true },
        { text: "Evaluación técnica trimestral", included: true },
        { text: "Asesoría personalizada", included: true },
        { text: "Acceso a eventos especiales", included: true }
      ],
      cta: "Elegir Elite"
    }
  ];

  return (
    <section id="pricing" className="py-16 sm:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 bg-yellow-50 text-yellow-700 rounded-full px-4 py-2 mb-4">
            <TrendingUp className="w-4 h-4" />
            <span className="text-sm font-semibold">INVERSIÓN EN SU FUTURO</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Elige el plan perfecto
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto mb-8">
            Sin costos ocultos. Sin contratos largos. Cancela cuando quieras.
          </p>

          {/* Billing Toggle */}
          <div className="inline-flex items-center gap-3 bg-gray-100 p-1 rounded-full">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${billingCycle === 'monthly' ? 'bg-white shadow text-gray-900' : 'text-gray-600'}`}
            >
              Mensual
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${billingCycle === 'yearly' ? 'bg-white shadow text-gray-900' : 'text-gray-600'}`}
            >
              Anual <span className="text-emerald-600 ml-1">-20%</span>
            </button>
          </div>
        </div>

        {/* Plans Grid */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {plans.map((plan, index) => (
            <div
              key={index}
              className={`relative rounded-3xl p-6 sm:p-8 ${
                plan.popular
                  ? 'bg-gradient-to-br from-gray-900 to-gray-800 text-white ring-4 ring-emerald-500'
                  : 'bg-white border-2 border-gray-200'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-emerald-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                    Más popular
                  </div>
                </div>
              )}

              <div className="text-center mb-6">
                <h3 className={`text-xl font-bold mb-1 ${plan.popular ? 'text-white' : 'text-gray-900'}`}>
                  {plan.name}
                </h3>
                <p className={`text-sm ${plan.popular ? 'text-gray-400' : 'text-gray-500'}`}>
                  {plan.description}
                </p>
              </div>

              <div className="text-center mb-6">
                <div className="flex items-baseline justify-center gap-1">
                  <span className={`text-4xl sm:text-5xl font-bold ${plan.popular ? 'text-white' : 'text-gray-900'}`}>
                    ${billingCycle === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice}
                  </span>
                  <span className={`${plan.popular ? 'text-gray-400' : 'text-gray-500'}`}>
                    /mes
                  </span>
                </div>
                {billingCycle === 'yearly' && (
                  <p className={`text-sm mt-1 ${plan.popular ? 'text-emerald-400' : 'text-emerald-600'}`}>
                    Facturado anualmente
                  </p>
                )}
              </div>

              <div className="space-y-3 mb-8">
                {plan.features.map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    {feature.included ? (
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                        plan.popular ? 'bg-emerald-500' : 'bg-emerald-100'
                      }`}>
                        <Check className={`w-3 h-3 ${plan.popular ? 'text-white' : 'text-emerald-600'}`} />
                      </div>
                    ) : (
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                        plan.popular ? 'bg-gray-700' : 'bg-gray-100'
                      }`}>
                        <Lock className={`w-3 h-3 ${plan.popular ? 'text-gray-500' : 'text-gray-400'}`} />
                      </div>
                    )}
                    <span className={`text-sm ${
                      feature.included
                        ? (plan.popular ? 'text-gray-300' : 'text-gray-700')
                        : (plan.popular ? 'text-gray-500' : 'text-gray-400')
                    }`}>
                      {feature.text}
                    </span>
                  </div>
                ))}
              </div>

              <button className={`w-full py-4 rounded-2xl font-bold text-lg transition-all ${
                plan.popular
                  ? 'bg-emerald-500 hover:bg-emerald-400 text-white hover:shadow-xl hover:shadow-emerald-500/30'
                  : 'bg-gray-900 hover:bg-gray-800 text-white'
              }`}>
                {plan.cta}
              </button>
            </div>
          ))}
        </div>

        {/* Payment methods */}
        <div className="mt-12 text-center">
          <p className="text-gray-500 text-sm mb-3">Aceptamos pagos de toda Latinoamérica</p>
          <div className="flex justify-center gap-4 text-gray-400">
            <span className="text-2xl">💳</span>
            <span className="text-2xl">🏦</span>
            <span className="text-2xl">📱</span>
          </div>
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const testimonials = [
    {
      name: "María González",
      role: "Mamá de Diego, 8 años",
      location: "Guadalajara, México",
      image: "👩",
      text: "Diego ha crecido muchísimo no solo como jugador, sino en confianza. Ver sus videos profesionales y su perfil en la web lo motiva a seguir mejorando cada día."
    },
    {
      name: "Carlos Rodríguez",
      role: "Papá de Mateo, 10 años",
      location: "Bogotá, Colombia",
      image: "👨",
      text: "Ya tuvimos el primer contacto de un scout gracias al perfil de Mateo. La inversión vale cada centavo cuando ves las oportunidades que se abren."
    },
    {
      name: "Ana Martínez",
      role: "Mamá de Valentina, 7 años",
      location: "Buenos Aires, Argentina",
      image: "👩",
      text: "Nunca imaginé que mi hija tendría un perfil profesional a los 7 años. ProKids no solo entrena, construye el futuro de nuestros hijos."
    }
  ];

  return (
    <section className="py-16 sm:py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Familias que confían en nosotros
          </h2>
          <p className="text-lg sm:text-xl text-gray-600">
            Historias reales de nuestra comunidad
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 sm:gap-8">
          {testimonials.map((testimonial, index) => (
            <div
              key={index}
              className="bg-white rounded-3xl p-6 sm:p-8 shadow-sm hover:shadow-xl transition-shadow"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center text-2xl">
                  {testimonial.image}
                </div>
                <div>
                  <div className="font-semibold text-gray-900">{testimonial.name}</div>
                  <div className="text-sm text-gray-500">{testimonial.role}</div>
                  <div className="text-sm text-emerald-600">{testimonial.location}</div>
                </div>
              </div>
              <p className="text-gray-600 leading-relaxed">"{testimonial.text}"</p>
              <div className="flex gap-1 mt-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// FAQ Section
const FAQSection = () => {
  const { t } = useLanguage();
  const [openIndex, setOpenIndex] = useState(0);

  const faqs = t.faq.items;

  return (
    <section className="py-16 sm:py-24 bg-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t.faq.title}
          </h2>
          <p className="text-lg sm:text-xl text-gray-600">
            {t.faq.subtitle}
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border border-gray-200 rounded-2xl overflow-hidden"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? -1 : index)}
                className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <span className="font-semibold text-gray-900 pr-4">{faq.question}</span>
                <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ${openIndex === index ? 'rotate-90' : ''}`} />
              </button>
              {openIndex === index && (
                <div className="px-6 pb-5">
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Sponsors Section
const SponsorsSection = () => {
  const sponsors = [
    {
      name: "Nike",
      logo: "https://upload.wikimedia.org/wikipedia/commons/a/a6/Logo_NIKE.svg",
      tier: "gold"
    },
    {
      name: "Adidas",
      logo: "https://upload.wikimedia.org/wikipedia/commons/2/20/Adidas_Logo.svg",
      tier: "gold"
    },
    {
      name: "Gatorade",
      logo: "https://upload.wikimedia.org/wikipedia/commons/f/f1/Gatorade_logo.svg",
      tier: "gold"
    },
    {
      name: "Puma",
      logo: "https://upload.wikimedia.org/wikipedia/commons/8/88/Puma_logo.svg",
      tier: "silver"
    },
    {
      name: "Under Armour",
      logo: "https://upload.wikimedia.org/wikipedia/commons/4/44/Under_armour_logo.svg",
      tier: "silver"
    },
    {
      name: "New Balance",
      logo: "https://upload.wikimedia.org/wikipedia/commons/e/ea/New_Balance_logo.svg",
      tier: "silver"
    }
  ];

  const goldSponsors = sponsors.filter(s => s.tier === 'gold');
  const silverSponsors = sponsors.filter(s => s.tier === 'silver');

  return (
    <section id="sponsors" className="py-16 sm:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 bg-yellow-50 text-yellow-700 rounded-full px-4 py-2 mb-4">
            <Star className="w-4 h-4 fill-yellow-500" />
            <span className="text-sm font-semibold">NUESTROS AUSPICIADORES</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Marcas que nos respaldan
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
            Grandes marcas confían en ProKids Talent y apoyan nuestros eventos para impulsar el talento juvenil de Latinoamérica.
          </p>
        </div>

        {/* Gold Sponsors */}
        <div className="mb-12">
          <div className="flex items-center justify-center gap-2 mb-8">
            <div className="h-px bg-gradient-to-r from-transparent via-yellow-400 to-transparent flex-1 max-w-[100px]"></div>
            <span className="text-yellow-600 font-semibold text-sm px-4 py-1 bg-yellow-50 rounded-full flex items-center gap-2">
              <Trophy className="w-4 h-4" />
              SPONSORS GOLD
            </span>
            <div className="h-px bg-gradient-to-r from-transparent via-yellow-400 to-transparent flex-1 max-w-[100px]"></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8">
            {goldSponsors.map((sponsor, index) => (
              <div
                key={index}
                className="group bg-gradient-to-br from-yellow-50 to-white border-2 border-yellow-200 rounded-2xl p-8 flex items-center justify-center hover:shadow-xl hover:border-yellow-400 transition-all duration-300 hover:-translate-y-1"
              >
                <div className="h-16 sm:h-20 flex items-center justify-center">
                  <img
                    src={sponsor.logo}
                    alt={`${sponsor.name} - Auspiciador Gold de ProKids Talent`}
                    className="max-h-full max-w-[180px] object-contain filter grayscale group-hover:grayscale-0 transition-all duration-300 opacity-70 group-hover:opacity-100"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = `<span class="text-2xl font-bold text-gray-400 group-hover:text-yellow-600 transition-colors">${sponsor.name}</span>`;
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Silver Sponsors */}
        <div className="mb-12">
          <div className="flex items-center justify-center gap-2 mb-8">
            <div className="h-px bg-gradient-to-r from-transparent via-gray-400 to-transparent flex-1 max-w-[100px]"></div>
            <span className="text-gray-600 font-semibold text-sm px-4 py-1 bg-gray-100 rounded-full flex items-center gap-2">
              <Award className="w-4 h-4" />
              SPONSORS SILVER
            </span>
            <div className="h-px bg-gradient-to-r from-transparent via-gray-400 to-transparent flex-1 max-w-[100px]"></div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 sm:gap-6">
            {silverSponsors.map((sponsor, index) => (
              <div
                key={index}
                className="group bg-gray-50 border border-gray-200 rounded-xl p-6 flex items-center justify-center hover:shadow-lg hover:border-gray-300 transition-all duration-300"
              >
                <div className="h-12 sm:h-14 flex items-center justify-center">
                  <img
                    src={sponsor.logo}
                    alt={`${sponsor.name} - Auspiciador Silver de ProKids Talent`}
                    className="max-h-full max-w-[140px] object-contain filter grayscale group-hover:grayscale-0 transition-all duration-300 opacity-60 group-hover:opacity-100"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = `<span class="text-xl font-bold text-gray-400 group-hover:text-gray-700 transition-colors">${sponsor.name}</span>`;
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Become a Sponsor CTA */}
        <div className="mt-12 bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-3xl p-6 sm:p-8 text-white">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="text-center sm:text-left">
              <h3 className="text-xl sm:text-2xl font-bold mb-2">¿Quieres ser auspiciador?</h3>
              <p className="text-emerald-100">
                Conecta tu marca con el futuro del fútbol latinoamericano. Miles de familias en nuestros eventos.
              </p>
            </div>
            <a
              href="https://wa.me/17869564198?text=Hola%2C%20me%20interesa%20ser%20auspiciador%20de%20ProKids%20Talent"
              target="_blank"
              rel="noopener noreferrer"
              className="flex-shrink-0 bg-white text-emerald-700 px-6 py-3 rounded-full font-semibold hover:bg-gray-100 transition-all hover:shadow-lg flex items-center gap-2"
            >
              <MessageCircle className="w-5 h-5" />
              Contactar ahora
            </a>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
          <div>
            <div className="text-3xl sm:text-4xl font-bold text-emerald-600">24+</div>
            <div className="text-gray-600 text-sm">Eventos anuales</div>
          </div>
          <div>
            <div className="text-3xl sm:text-4xl font-bold text-emerald-600">50K+</div>
            <div className="text-gray-600 text-sm">Asistentes</div>
          </div>
          <div>
            <div className="text-3xl sm:text-4xl font-bold text-emerald-600">12</div>
            <div className="text-gray-600 text-sm">Países LATAM</div>
          </div>
          <div>
            <div className="text-3xl sm:text-4xl font-bold text-emerald-600">2M+</div>
            <div className="text-gray-600 text-sm">Alcance en redes</div>
          </div>
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => {
  const { t } = useLanguage();
  return (
  <section className="py-16 sm:py-24 bg-gradient-to-br from-emerald-600 to-emerald-800 relative overflow-hidden">
    <div className="absolute inset-0 opacity-10">
      <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full filter blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-yellow-400 rounded-full filter blur-3xl"></div>
    </div>

    <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6">
        {t.cta.title1}
        <br />
        <span className="text-emerald-200">{t.cta.title2}</span>
      </h2>
      <p className="text-lg sm:text-xl text-emerald-100 mb-8 max-w-2xl mx-auto">
        {t.cta.subtitle}
      </p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <button className="bg-white text-emerald-700 px-8 py-4 rounded-full font-bold text-lg hover:bg-gray-100 transition-all hover:shadow-xl flex items-center justify-center gap-2 group">
          {t.cta.button1}
          <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
        </button>
        <a href="https://wa.me/17869564198?text=Hola%2C%20quiero%20información%20sobre%20ProKids%20Talent" target="_blank" rel="noopener noreferrer" className="bg-emerald-700/50 border border-emerald-400/30 text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-emerald-700 transition-all flex items-center justify-center gap-2">
          <Play className="w-5 h-5" />
          {t.cta.button2}
        </a>
      </div>
    </div>
  </section>
  );
};

// Footer
const Footer = () => {
  const { t } = useLanguage();
  return (
  <footer className="bg-gray-900 text-white py-12 sm:py-16">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
        <div>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-xl flex items-center justify-center">
              <Trophy className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="text-lg font-bold">ProKids</span>
              <span className="text-lg font-light text-emerald-400"> Talent</span>
            </div>
          </div>
          <p className="text-gray-400 text-sm leading-relaxed">
            {t.footer.description}
          </p>
        </div>

        <div>
          <h4 className="font-semibold mb-4">{t.footer.links.title}</h4>
          <ul className="space-y-2 text-gray-400">
            <li><a href="#como-funciona" className="hover:text-white transition-colors">{t.footer.links.howItWorks}</a></li>
            <li><a href="#pricing" className="hover:text-white transition-colors">{t.footer.links.pricing}</a></li>
            <li><a href="#" className="hover:text-white transition-colors">{t.footer.links.players}</a></li>
            <li><a href="#" className="hover:text-white transition-colors">{t.footer.links.blog}</a></li>
          </ul>
        </div>

        <div>
          <h4 className="font-semibold mb-4">{t.footer.legal.title}</h4>
          <ul className="space-y-2 text-gray-400">
            <li><a href="#" className="hover:text-white transition-colors">{t.footer.legal.terms}</a></li>
            <li><a href="#" className="hover:text-white transition-colors">{t.footer.legal.privacy}</a></li>
            <li><a href="#" className="hover:text-white transition-colors">{t.footer.legal.minors}</a></li>
          </ul>
        </div>

        <div>
          <h4 className="font-semibold mb-4">{t.footer.social.title}</h4>
          <div className="flex gap-4">
            <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-emerald-600 transition-colors">
              <Instagram className="w-5 h-5" />
            </a>
            <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-emerald-600 transition-colors">
              <Youtube className="w-5 h-5" />
            </a>
            <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-emerald-600 transition-colors">
              <Twitter className="w-5 h-5" />
            </a>
          </div>
          <div className="mt-6">
            <p className="text-gray-400 text-sm">{t.footer.questions}</p>
            <a href="mailto:hola@prokidstalent.com" className="text-emerald-400 hover:text-emerald-300 transition-colors">
              hola@prokidstalent.com
            </a>
          </div>
        </div>
      </div>

      <div className="border-t border-gray-800 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
        <p className="text-gray-500 text-sm">
          {t.footer.copyright}
        </p>
        <div className="flex items-center gap-2 text-gray-500 text-sm">
          <span>🇲🇽</span>
          <span>🇨🇴</span>
          <span>🇦🇷</span>
          <span>🇨🇱</span>
          <span>🇵🇪</span>
          <span className="ml-2">{t.footer.allLatam}</span>
        </div>
      </div>
    </div>
  </footer>
  );
};

// Contact Page
const ContactPage = ({ setCurrentPage }) => {
  const [formData, setFormData] = useState({
    parentName: '',
    email: '',
    phone: '',
    childName: '',
    childAge: '',
    city: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // ✅ Backend API — reemplaza EmailJS (sin keys expuestas en el browser)
      await contactApi.send({
        name: formData.parentName,
        email: formData.email,
        phone: formData.phone,
        playerName: formData.childName,
        playerAge: formData.childAge ? parseInt(formData.childAge) : undefined,
        city: formData.city,
        message: formData.message || `Interesado en ProKids Talent para ${formData.childName}`,
        inquiryType: 'GENERAL',
      });

      setSubmitStatus('success');
      setFormData({
        parentName: '',
        email: '',
        phone: '',
        childName: '',
        childAge: '',
        city: '',
        message: ''
      });
    } catch (error) {
      console.error('Error sending contact:', error);
      setSubmitStatus('error');
    }

    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-br from-gray-900 via-emerald-900 to-gray-900 pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => setCurrentPage('home')}
            className="flex items-center gap-2 text-white/70 hover:text-white mb-8 transition-colors"
          >
            <ChevronRight className="w-5 h-5 rotate-180" />
            Volver al inicio
          </button>

          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-emerald-500/20 text-emerald-400 rounded-full px-4 py-2 mb-4">
              <Mail className="w-4 h-4" />
              <span className="text-sm font-semibold">CONTÁCTANOS</span>
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
              Inscribe a tu hijo hoy
            </h1>
            <p className="text-lg text-white/70 max-w-2xl mx-auto">
              Completa el formulario y nos pondremos en contacto contigo en menos de 24 horas.
            </p>
          </div>
        </div>
      </div>

      {/* Contact Form */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 pb-16">
        <div className="grid md:grid-cols-3 gap-8">
          {/* Form */}
          <div className="md:col-span-2">
            <div className="bg-white rounded-3xl shadow-xl p-6 sm:p-8">
              {submitStatus === 'success' ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">¡Mensaje enviado!</h3>
                  <p className="text-gray-600 mb-6">
                    Gracias por tu interés. Te contactaremos pronto.
                  </p>
                  <button
                    onClick={() => setSubmitStatus(null)}
                    className="bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-3 rounded-full font-semibold transition-all"
                  >
                    Enviar otro mensaje
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <h2 className="text-xl font-bold text-gray-900 mb-6">Datos del padre/madre</h2>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nombre completo *
                      </label>
                      <input
                        type="text"
                        name="parentName"
                        value={formData.parentName}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                        placeholder="Tu nombre"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Correo electrónico *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                        placeholder="tu@email.com"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Teléfono / WhatsApp *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                        placeholder="+52 55 1234 5678"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Ciudad / País *
                      </label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                        placeholder="Ciudad de México, México"
                      />
                    </div>
                  </div>

                  <h2 className="text-xl font-bold text-gray-900 mb-4 pt-4">Datos del jugador</h2>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nombre del niño/a *
                      </label>
                      <input
                        type="text"
                        name="childName"
                        value={formData.childName}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                        placeholder="Nombre del jugador"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Edad *
                      </label>
                      <select
                        name="childAge"
                        value={formData.childAge}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                      >
                        <option value="">Selecciona la edad</option>
                        <option value="6">6 años</option>
                        <option value="7">7 años</option>
                        <option value="8">8 años</option>
                        <option value="9">9 años</option>
                        <option value="10">10 años</option>
                        <option value="11">11 años</option>
                        <option value="12">12 años</option>
                        <option value="13">13 años</option>
                        <option value="14">14 años</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Mensaje (opcional)
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all resize-none"
                      placeholder="¿Tienes alguna pregunta o comentario?"
                    />
                  </div>

                  {submitStatus === 'error' && (
                    <div className="bg-red-50 text-red-700 px-4 py-3 rounded-xl text-sm">
                      Hubo un error al enviar el mensaje. Por favor intenta de nuevo o contáctanos por WhatsApp.
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-400 text-white py-4 rounded-xl font-bold text-lg transition-all flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Enviar solicitud
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact Info */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="font-bold text-gray-900 mb-4">Contacto directo</h3>
              <div className="space-y-4">
                <a
                  href="https://wa.me/17869564198?text=Hola%2C%20quiero%20información%20sobre%20ProKids%20Talent"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-gray-600 hover:text-emerald-600 transition-colors"
                >
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                  </div>
                  <div>
                    <div className="font-medium">WhatsApp</div>
                    <div className="text-sm text-gray-500">+1 (786) 956-4198</div>
                  </div>
                </a>
                <a
                  href="mailto:hola@prokidstalent.com"
                  className="flex items-center gap-3 text-gray-600 hover:text-emerald-600 transition-colors"
                >
                  <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                    <Mail className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <div className="font-medium">Email</div>
                    <div className="text-sm text-gray-500">hola@prokidstalent.com</div>
                  </div>
                </a>
              </div>
            </div>

            {/* Why ProKids */}
            <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl shadow-lg p-6 text-white">
              <h3 className="font-bold mb-4">¿Por qué ProKids?</h3>
              <ul className="space-y-3 text-sm">
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span>Sistema integral de desarrollo</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span>Contenido profesional incluido</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span>Representación deportiva</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span>Sin contratos a largo plazo</span>
                </li>
              </ul>
            </div>

            {/* Response Time */}
            <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
              <div className="text-3xl mb-2">⏰</div>
              <h3 className="font-bold text-gray-900">Respuesta rápida</h3>
              <p className="text-sm text-gray-600 mt-1">
                Te contactamos en menos de 24 horas
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gray-900 text-white py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-400 text-sm">
            © 2025 <span className="text-emerald-400 font-semibold">ProKids Talent</span>. Todos los derechos reservados.
          </p>
        </div>
      </div>
    </div>
  );
};

// Player Registration Page with 4-step flow
const PlayerRegistrationPage = ({ setCurrentPage }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    // Datos del padre/tutor
    parentName: '',
    parentEmail: '',
    parentPhone: '',
    relationship: 'padre',
    // Datos del jugador
    playerName: '',
    playerAge: '',
    birthDate: '',
    position: '',
    dominantFoot: 'derecho',
    height: '',
    weight: '',
    city: '',
    country: '',
    // Experiencia
    currentTeam: '',
    yearsPlaying: '',
    achievements: '',
    // Bio y adicionales
    bio: '',
    socialMedia: '',
    howDidYouHear: '',
    acceptTerms: false
  });

  const steps = [
    { number: 1, title: 'Información', icon: <FileText className="w-5 h-5" /> },
    { number: 2, title: 'Datos Recibidos', icon: <CheckCircle className="w-5 h-5" /> },
    { number: 3, title: 'Revisión', icon: <Eye className="w-5 h-5" /> },
    { number: 4, title: 'Publicación', icon: <Globe className="w-5 h-5" /> }
  ];

  const positions = [
    'Portero',
    'Defensa Central',
    'Lateral Derecho',
    'Lateral Izquierdo',
    'Mediocampista Defensivo',
    'Mediocampista Central',
    'Mediocampista Ofensivo',
    'Extremo Derecho',
    'Extremo Izquierdo',
    'Delantero Centro',
    'Segundo Delantero'
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmitStep1 = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simular envío
    await new Promise(resolve => setTimeout(resolve, 1500));

    setIsSubmitting(false);
    setCurrentStep(2);
  };

  const handleRequestReview = () => {
    setCurrentStep(3);
  };

  const handlePublish = async () => {
    setIsSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 2000));
    setIsSubmitting(false);
    setCurrentStep(4);
  };

  // Step 1: Formulario de registro
  const renderStep1 = () => (
    <form onSubmit={handleSubmitStep1} className="space-y-8">
      {/* Datos del Padre/Tutor */}
      <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg">
        <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
          <User className="w-5 h-5 text-emerald-600" />
          Datos del Padre/Tutor
        </h2>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nombre completo *
            </label>
            <input
              type="text"
              name="parentName"
              value={formData.parentName}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="Nombre del padre o tutor"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Relación con el jugador *
            </label>
            <select
              name="relationship"
              value={formData.relationship}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            >
              <option value="padre">Padre</option>
              <option value="madre">Madre</option>
              <option value="tutor">Tutor legal</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Correo electrónico *
            </label>
            <input
              type="email"
              name="parentEmail"
              value={formData.parentEmail}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="tu@email.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Teléfono / WhatsApp *
            </label>
            <input
              type="tel"
              name="parentPhone"
              value={formData.parentPhone}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="+52 55 1234 5678"
            />
          </div>
        </div>
      </div>

      {/* Datos del Jugador */}
      <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg">
        <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
          <Trophy className="w-5 h-5 text-emerald-600" />
          Datos del Jugador
        </h2>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nombre completo del jugador *
            </label>
            <input
              type="text"
              name="playerName"
              value={formData.playerName}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="Nombre del niño/a"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fecha de nacimiento *
            </label>
            <input
              type="date"
              name="birthDate"
              value={formData.birthDate}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Edad *
            </label>
            <select
              name="playerAge"
              value={formData.playerAge}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            >
              <option value="">Selecciona la edad</option>
              {[6,7,8,9,10,11,12,13,14].map(age => (
                <option key={age} value={age}>{age} años</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Posición principal *
            </label>
            <select
              name="position"
              value={formData.position}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            >
              <option value="">Selecciona posición</option>
              {positions.map(pos => (
                <option key={pos} value={pos}>{pos}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Pie dominante *
            </label>
            <select
              name="dominantFoot"
              value={formData.dominantFoot}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            >
              <option value="derecho">Derecho</option>
              <option value="izquierdo">Izquierdo</option>
              <option value="ambidiestro">Ambidiestro</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Altura (cm)
            </label>
            <input
              type="number"
              name="height"
              value={formData.height}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="Ej: 138"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Peso (kg)
            </label>
            <input
              type="number"
              name="weight"
              value={formData.weight}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="Ej: 32"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Ciudad *
            </label>
            <input
              type="text"
              name="city"
              value={formData.city}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="Ciudad de México"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              País *
            </label>
            <select
              name="country"
              value={formData.country}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            >
              <option value="">Selecciona país</option>
              <option value="México">México</option>
              <option value="Colombia">Colombia</option>
              <option value="Argentina">Argentina</option>
              <option value="Chile">Chile</option>
              <option value="Perú">Perú</option>
              <option value="Ecuador">Ecuador</option>
              <option value="Venezuela">Venezuela</option>
              <option value="Brasil">Brasil</option>
              <option value="Otro">Otro</option>
            </select>
          </div>
        </div>
      </div>

      {/* Experiencia Deportiva */}
      <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg">
        <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
          <Award className="w-5 h-5 text-emerald-600" />
          Experiencia Deportiva
        </h2>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Equipo actual (si aplica)
            </label>
            <input
              type="text"
              name="currentTeam"
              value={formData.currentTeam}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="Nombre del equipo"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Años jugando fútbol
            </label>
            <select
              name="yearsPlaying"
              value={formData.yearsPlaying}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            >
              <option value="">Selecciona</option>
              <option value="menos1">Menos de 1 año</option>
              <option value="1-2">1-2 años</option>
              <option value="3-4">3-4 años</option>
              <option value="5+">5+ años</option>
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Logros o reconocimientos
            </label>
            <textarea
              name="achievements"
              value={formData.achievements}
              onChange={handleChange}
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
              placeholder="Torneos ganados, premios, reconocimientos..."
            />
          </div>
        </div>
      </div>

      {/* Biografía */}
      <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg">
        <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
          <FileText className="w-5 h-5 text-emerald-600" />
          Biografía del Jugador
        </h2>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cuéntanos sobre el jugador *
            </label>
            <textarea
              name="bio"
              value={formData.bio}
              onChange={handleChange}
              required
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
              placeholder="¿Cuándo empezó a jugar? ¿Qué lo motiva? ¿Cuáles son sus sueños en el fútbol?"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Redes sociales del jugador (opcional)
            </label>
            <input
              type="text"
              name="socialMedia"
              value={formData.socialMedia}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="@instagram, canal de YouTube, etc."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              ¿Cómo te enteraste de ProKids Talent?
            </label>
            <select
              name="howDidYouHear"
              value={formData.howDidYouHear}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            >
              <option value="">Selecciona</option>
              <option value="instagram">Instagram</option>
              <option value="facebook">Facebook</option>
              <option value="youtube">YouTube</option>
              <option value="google">Búsqueda en Google</option>
              <option value="amigo">Recomendación de amigo</option>
              <option value="otro">Otro</option>
            </select>
          </div>
        </div>
      </div>

      {/* Términos y condiciones */}
      <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            name="acceptTerms"
            checked={formData.acceptTerms}
            onChange={handleChange}
            required
            className="mt-1 w-5 h-5 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
          />
          <span className="text-gray-700 text-sm">
            Acepto los <a href="#" className="text-emerald-600 underline">términos y condiciones</a> y la{' '}
            <a href="#" className="text-emerald-600 underline">política de privacidad</a> de ProKids Talent.
            Autorizo el uso de la imagen y datos del menor para fines deportivos y de representación.
          </span>
        </label>
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-400 text-white py-4 rounded-2xl font-bold text-lg transition-all flex items-center justify-center gap-2"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Enviando datos...
          </>
        ) : (
          <>
            <Send className="w-5 h-5" />
            Enviar formulario
          </>
        )}
      </button>
    </form>
  );

  // Step 2: Confirmación de datos recibidos
  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
        <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-10 h-10 text-emerald-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">¡Datos recibidos correctamente!</h2>
        <p className="text-gray-600 mb-6">
          Hemos recibido la información del perfil de <strong>{formData.playerName}</strong>.
          Ahora nuestro equipo revisará los datos antes de la publicación.
        </p>

        <div className="bg-emerald-50 rounded-xl p-4 mb-6 text-left">
          <h3 className="font-semibold text-emerald-800 mb-2">Próximos pasos:</h3>
          <ul className="text-emerald-700 text-sm space-y-1">
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4" />
              Verificación de datos por nuestro equipo
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4" />
              Contacto para confirmar detalles (si es necesario)
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4" />
              Aprobación y publicación del perfil
            </li>
          </ul>
        </div>

        <div className="text-sm text-gray-500 mb-6">
          <Clock className="w-4 h-4 inline mr-1" />
          Tiempo estimado de revisión: 24-48 horas
        </div>

        <button
          onClick={handleRequestReview}
          className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-3 rounded-xl font-semibold transition-all"
        >
          Ver resumen de datos
        </button>
      </div>

      {/* Summary Card */}
      <div className="bg-white rounded-2xl p-6 shadow-lg">
        <h3 className="font-bold text-gray-900 mb-4">Resumen de la solicitud</h3>
        <div className="grid sm:grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-500">Jugador:</span>
            <p className="font-medium">{formData.playerName}</p>
          </div>
          <div>
            <span className="text-gray-500">Edad:</span>
            <p className="font-medium">{formData.playerAge} años</p>
          </div>
          <div>
            <span className="text-gray-500">Posición:</span>
            <p className="font-medium">{formData.position}</p>
          </div>
          <div>
            <span className="text-gray-500">Ubicación:</span>
            <p className="font-medium">{formData.city}, {formData.country}</p>
          </div>
          <div>
            <span className="text-gray-500">Contacto:</span>
            <p className="font-medium">{formData.parentEmail}</p>
          </div>
          <div>
            <span className="text-gray-500">Estado:</span>
            <p className="font-medium text-yellow-600 flex items-center gap-1">
              <Clock className="w-4 h-4" />
              Pendiente de revisión
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  // Step 3: Revisión de datos (simulado como admin view)
  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="font-semibold text-yellow-800">En revisión por el equipo</h3>
          <p className="text-yellow-700 text-sm">
            Un administrador está revisando los datos del perfil. Una vez aprobado, se publicará automáticamente.
          </p>
        </div>
      </div>

      {/* Full Profile Preview */}
      <div className="bg-white rounded-2xl overflow-hidden shadow-lg">
        <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 p-6 text-white">
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center text-4xl">
              ⚽
            </div>
            <div>
              <h2 className="text-2xl font-bold">{formData.playerName}</h2>
              <p className="text-emerald-100">{formData.position} · {formData.playerAge} años</p>
              <p className="text-emerald-200 text-sm">{formData.city}, {formData.country}</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Bio */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-2">Biografía</h3>
            <p className="text-gray-600">{formData.bio || 'Sin biografía'}</p>
          </div>

          {/* Physical Stats */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-gray-50 rounded-xl p-4 text-center">
              <Ruler className="w-5 h-5 text-emerald-600 mx-auto mb-1" />
              <p className="text-2xl font-bold text-gray-900">{formData.height || '--'}</p>
              <p className="text-gray-500 text-sm">cm</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 text-center">
              <Scale className="w-5 h-5 text-emerald-600 mx-auto mb-1" />
              <p className="text-2xl font-bold text-gray-900">{formData.weight || '--'}</p>
              <p className="text-gray-500 text-sm">kg</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 text-center">
              <Footprints className="w-5 h-5 text-emerald-600 mx-auto mb-1" />
              <p className="text-lg font-bold text-gray-900 capitalize">{formData.dominantFoot}</p>
              <p className="text-gray-500 text-sm">Pie</p>
            </div>
          </div>

          {/* Experience */}
          {formData.currentTeam && (
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Equipo Actual</h3>
              <p className="text-gray-600">{formData.currentTeam}</p>
            </div>
          )}

          {formData.achievements && (
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Logros</h3>
              <p className="text-gray-600">{formData.achievements}</p>
            </div>
          )}

          {/* Contact Info (Admin view) */}
          <div className="border-t pt-4">
            <h3 className="font-semibold text-gray-900 mb-2">Información de contacto (Solo admin)</h3>
            <div className="grid sm:grid-cols-2 gap-2 text-sm text-gray-600">
              <p><strong>Padre/Tutor:</strong> {formData.parentName}</p>
              <p><strong>Relación:</strong> {formData.relationship}</p>
              <p><strong>Email:</strong> {formData.parentEmail}</p>
              <p><strong>Teléfono:</strong> {formData.parentPhone}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Admin Actions (simulated) */}
      <div className="flex gap-4">
        <button
          onClick={handlePublish}
          disabled={isSubmitting}
          className="flex-1 bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-400 text-white py-4 rounded-xl font-bold transition-all flex items-center justify-center gap-2"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Publicando...
            </>
          ) : (
            <>
              <Check className="w-5 h-5" />
              Aprobar y Publicar
            </>
          )}
        </button>
      </div>
    </div>
  );

  // Step 4: Perfil publicado
  const renderStep4 = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
        <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <Globe className="w-10 h-10 text-emerald-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">¡Perfil publicado exitosamente!</h2>
        <p className="text-gray-600 mb-6">
          El perfil de <strong>{formData.playerName}</strong> ya está disponible en la web y será
          indexado por Google para que scouts y equipos puedan encontrarlo.
        </p>

        <div className="bg-gray-100 rounded-xl p-4 mb-6">
          <p className="text-sm text-gray-500 mb-1">URL del perfil:</p>
          <p className="text-emerald-600 font-medium break-all">
            prokidstalent.com/jugadores/{formData.playerName.toLowerCase().replace(/\s+/g, '-')}
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={() => setCurrentPage('player')}
            className="bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2"
          >
            <Eye className="w-5 h-5" />
            Ver perfil de ejemplo
          </button>
          <button
            onClick={() => setCurrentPage('home')}
            className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-6 py-3 rounded-xl font-semibold transition-all"
          >
            Volver al inicio
          </button>
        </div>
      </div>

      {/* Success Tips */}
      <div className="bg-emerald-50 rounded-2xl p-6">
        <h3 className="font-bold text-emerald-800 mb-4">🎉 ¿Qué sigue?</h3>
        <ul className="space-y-3 text-emerald-700">
          <li className="flex items-start gap-3">
            <div className="w-6 h-6 bg-emerald-200 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-emerald-700 font-bold text-sm">1</span>
            </div>
            <span>Comparte el perfil en redes sociales para aumentar la visibilidad</span>
          </li>
          <li className="flex items-start gap-3">
            <div className="w-6 h-6 bg-emerald-200 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-emerald-700 font-bold text-sm">2</span>
            </div>
            <span>Asiste a las jornadas mensuales para generar contenido profesional</span>
          </li>
          <li className="flex items-start gap-3">
            <div className="w-6 h-6 bg-emerald-200 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-emerald-700 font-bold text-sm">3</span>
            </div>
            <span>Te notificaremos cuando scouts o equipos muestren interés</span>
          </li>
        </ul>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-br from-gray-900 via-emerald-900 to-gray-900 pt-24 pb-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => setCurrentPage('home')}
            className="flex items-center gap-2 text-white/70 hover:text-white mb-6 transition-colors"
          >
            <ChevronRight className="w-5 h-5 rotate-180" />
            Volver al inicio
          </button>

          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-emerald-500/20 text-emerald-400 rounded-full px-4 py-2 mb-4">
              <User className="w-4 h-4" />
              <span className="text-sm font-semibold">REGISTRO DE JUGADOR</span>
            </div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-2">
              Crea el perfil de tu hijo
            </h1>
            <p className="text-white/70">
              Completa el formulario y empieza a construir su carrera
            </p>
          </div>

          {/* Progress Steps */}
          <div className="flex justify-between items-center max-w-xl mx-auto">
            {steps.map((step, index) => (
              <div key={step.number} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all ${
                    currentStep >= step.number
                      ? 'bg-emerald-500 text-white'
                      : 'bg-white/20 text-white/50'
                  }`}>
                    {currentStep > step.number ? (
                      <Check className="w-5 h-5" />
                    ) : (
                      step.icon
                    )}
                  </div>
                  <span className={`text-xs mt-2 hidden sm:block ${
                    currentStep >= step.number ? 'text-white' : 'text-white/50'
                  }`}>
                    {step.title}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div className={`w-12 sm:w-20 h-1 mx-2 rounded ${
                    currentStep > step.number ? 'bg-emerald-500' : 'bg-white/20'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 -mt-4">
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}
        {currentStep === 4 && renderStep4()}
      </div>

      {/* Footer */}
      <div className="bg-gray-900 text-white py-8 mt-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-400 text-sm">
            © 2025 <span className="text-emerald-400 font-semibold">ProKids Talent</span>. Todos los derechos reservados.
          </p>
        </div>
      </div>
    </div>
  );
};

// Player Profile Page
const PlayerProfilePage = ({ setCurrentPage }) => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with back button */}
      <div className="bg-gradient-to-br from-gray-900 via-emerald-900 to-gray-900 pt-24 pb-32">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => setCurrentPage('home')}
            className="flex items-center gap-2 text-white/70 hover:text-white mb-8 transition-colors"
          >
            <ChevronRight className="w-5 h-5 rotate-180" />
            Volver al inicio
          </button>

          <div className="flex flex-col md:flex-row gap-8 items-start">
            {/* Player Photo */}
            <div className="w-32 h-32 sm:w-40 sm:h-40 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-3xl flex items-center justify-center text-6xl sm:text-7xl shadow-2xl flex-shrink-0">
              ⚽
            </div>

            {/* Player Info */}
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <span className="bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                  VERIFICADO
                </span>
                <span className="text-emerald-400 text-sm">Miembro desde {samplePlayer.since}</span>
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-2">
                {samplePlayer.name}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-white/70 mb-4">
                <span className="flex items-center gap-1">
                  <User className="w-4 h-4" />
                  {samplePlayer.age} años
                </span>
                <span className="flex items-center gap-1">
                  <Trophy className="w-4 h-4" />
                  {samplePlayer.position}
                </span>
                <span className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  {samplePlayer.location}
                </span>
              </div>
              <p className="text-white/60 max-w-2xl leading-relaxed">
                {samplePlayer.bio}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16">
        <div className="bg-white rounded-2xl shadow-xl p-6 grid grid-cols-2 sm:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="text-2xl sm:text-3xl font-bold text-gray-900">{samplePlayer.stats.sessions}</div>
            <div className="text-gray-500 text-sm">Sesiones</div>
          </div>
          <div className="text-center">
            <div className="text-2xl sm:text-3xl font-bold text-gray-900">{samplePlayer.stats.videos}</div>
            <div className="text-gray-500 text-sm">Videos</div>
          </div>
          <div className="text-center">
            <div className="text-2xl sm:text-3xl font-bold text-gray-900">{samplePlayer.stats.views}</div>
            <div className="text-gray-500 text-sm">Vistas</div>
          </div>
          <div className="text-center">
            <div className="text-2xl sm:text-3xl font-bold text-gray-900">{samplePlayer.stats.followers}</div>
            <div className="text-gray-500 text-sm">Seguidores</div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-8">
            {/* Videos */}
            <div className="bg-white rounded-2xl p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Video className="w-5 h-5 text-emerald-600" />
                Videos Destacados
              </h2>
              <div className="grid sm:grid-cols-3 gap-4">
                {samplePlayer.videos.map((video, index) => (
                  <div key={index} className="group cursor-pointer">
                    <div className="aspect-video bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl flex items-center justify-center text-4xl mb-2 relative overflow-hidden">
                      {video.thumbnail}
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <Play className="w-12 h-12 text-white" />
                      </div>
                    </div>
                    <h3 className="font-medium text-gray-900 text-sm">{video.title}</h3>
                    <p className="text-gray-500 text-xs">{video.views} vistas</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Skills */}
            <div className="bg-white rounded-2xl p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
                Habilidades
              </h2>
              <div className="space-y-4">
                {samplePlayer.skills.map((skill, index) => (
                  <div key={index}>
                    <div className="flex justify-between mb-1">
                      <span className="text-gray-700">{skill.name}</span>
                      <span className="text-emerald-600 font-semibold">{skill.level}%</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 rounded-full transition-all duration-500"
                        style={{ width: `${skill.level}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Physical Stats */}
            <div className="bg-white rounded-2xl p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Datos Físicos</h2>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-gray-600">
                    <Ruler className="w-4 h-4" />
                    Altura
                  </span>
                  <span className="font-semibold">{samplePlayer.height}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-gray-600">
                    <Scale className="w-4 h-4" />
                    Peso
                  </span>
                  <span className="font-semibold">{samplePlayer.weight}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-gray-600">
                    <Footprints className="w-4 h-4" />
                    Pie dominante
                  </span>
                  <span className="font-semibold">{samplePlayer.dominantFoot}</span>
                </div>
              </div>
            </div>

            {/* Achievements */}
            <div className="bg-white rounded-2xl p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Award className="w-5 h-5 text-yellow-500" />
                Logros
              </h2>
              <div className="space-y-3">
                {samplePlayer.achievements.map((achievement, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                    </div>
                    <span className="text-gray-700 text-sm">{achievement}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Contact CTA */}
            <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl p-6 text-white">
              <h2 className="text-lg font-bold mb-2">¿Interesado en este jugador?</h2>
              <p className="text-emerald-100 text-sm mb-4">
                Contacta a nuestro equipo de representación para más información.
              </p>
              <button className="w-full bg-white text-emerald-700 py-3 rounded-xl font-semibold hover:bg-gray-100 transition-colors">
                Contactar representante
              </button>
            </div>

            {/* Social Links */}
            <div className="bg-white rounded-2xl p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Redes Sociales</h2>
              <div className="flex gap-3">
                <a href="#" className="flex-1 flex items-center justify-center gap-2 bg-red-50 text-red-600 py-3 rounded-xl hover:bg-red-100 transition-colors">
                  <Youtube className="w-5 h-5" />
                </a>
                <a href="#" className="flex-1 flex items-center justify-center gap-2 bg-pink-50 text-pink-600 py-3 rounded-xl hover:bg-pink-100 transition-colors">
                  <Instagram className="w-5 h-5" />
                </a>
                <a href="#" className="flex-1 flex items-center justify-center gap-2 bg-blue-50 text-blue-600 py-3 rounded-xl hover:bg-blue-100 transition-colors">
                  <Twitter className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer on profile page */}
      <div className="bg-gray-900 text-white py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-400 text-sm">
            Perfil verificado y representado por{' '}
            <span className="text-emerald-400 font-semibold">ProKids Talent</span>
          </p>
        </div>
      </div>
    </div>
  );
};

// ─── Alto Rendimiento Page ──────────────────────────────────────────────────
const AltoRendimientoPage = ({ setCurrentPage }) => {
  const { t } = useLanguage();
  const ar = t.altoRendimiento || {};

  const trainingBlocks = [
    { time: '16:30 – 16:40', title: ar.block1Title || 'Activación dinámica', desc: ar.block1Desc || 'Calentamiento con balón, movilidad articular y juegos de activación neuromuscular.', icon: '🔥', min: '10 min' },
    { time: '16:40 – 16:55', title: ar.block2Title || 'Coordinación y motricidad', desc: ar.block2Desc || 'Circuitos de agilidad, equilibrio, velocidad de reacción y trabajo de pies (ABC del movimiento).', icon: '⚡', min: '15 min' },
    { time: '16:55 – 17:20', title: ar.block3Title || 'Técnica individual', desc: ar.block3Desc || 'Conducción, control orientado, pase corto/largo y remate. Trabajo en estaciones con corrección personalizada.', icon: '⚽', min: '25 min' },
    { time: '17:20 – 17:45', title: ar.block4Title || 'Juegos reducidos', desc: ar.block4Desc || 'Situaciones 1v1, 2v2 y 3v3 para aplicar la técnica bajo presión real. Toma de decisiones en espacios reducidos.', icon: '🎯', min: '25 min' },
    { time: '17:45 – 18:10', title: ar.block5Title || 'Táctica y juego dirigido', desc: ar.block5Desc || 'Partidos 5v5/7v7 con consignas tácticas: movimiento sin balón, presión, transiciones y creación de espacios.', icon: '🧠', min: '25 min' },
    { time: '18:10 – 18:25', title: ar.block6Title || 'Partido libre + desafíos', desc: ar.block6Desc || 'Competencia libre con desafíos individuales y grupales. Penales, tiros libres y challenges técnicos.', icon: '🏆', min: '15 min' },
    { time: '18:25 – 18:30', title: ar.block7Title || 'Vuelta a la calma y feedback', desc: ar.block7Desc || 'Elongación, reflexión grupal y feedback personalizado de los entrenadores.', icon: '💬', min: '5 min' },
  ];

  const tests = [
    { name: ar.test1 || 'Sprint 20m', desc: ar.test1Desc || 'Velocidad explosiva en línea recta', icon: '🏃' },
    { name: ar.test2 || 'T-Test agilidad', desc: ar.test2Desc || 'Cambios de dirección y velocidad lateral', icon: '↔️' },
    { name: ar.test3 || 'Salto horizontal', desc: ar.test3Desc || 'Potencia de tren inferior', icon: '📏' },
    { name: ar.test4 || 'Slalom con balón', desc: ar.test4Desc || 'Conducción técnica bajo presión de tiempo', icon: '🔀' },
    { name: ar.test5 || 'Pase a diana', desc: ar.test5Desc || 'Precisión de pase corto y largo', icon: '🎯' },
    { name: ar.test6 || 'Remate a portería', desc: ar.test6Desc || 'Finalización: potencia y precisión', icon: '🥅' },
  ];

  const pillars = [
    { title: ar.pillar1 || 'Técnica', desc: ar.pillar1Desc || 'Dominio del balón, pase, control y remate con metodología progresiva.', icon: '⚽' },
    { title: ar.pillar2 || 'Físico', desc: ar.pillar2Desc || 'Preparación física adaptada a la edad: velocidad, coordinación y resistencia.', icon: '💪' },
    { title: ar.pillar3 || 'Táctico', desc: ar.pillar3Desc || 'Lectura del juego, toma de decisiones y comprensión de espacios.', icon: '🧠' },
    { title: ar.pillar4 || 'Mental', desc: ar.pillar4Desc || 'Concentración, disciplina, resiliencia y trabajo en equipo.', icon: '🎯' },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* ── Hero ─────────────────────────────────────────────────────────────── */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-gray-900 via-emerald-950 to-gray-900">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-500 rounded-full blur-[120px]" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-emerald-400 rounded-full blur-[150px]" />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-4 text-center pt-24 pb-16">
          <div className="inline-flex items-center gap-2 bg-emerald-500/20 border border-emerald-400/30 rounded-full px-4 py-1.5 mb-6">
            <span className="text-emerald-400 font-semibold text-sm tracking-wide uppercase">{ar.heroBadge || 'Nuevo Programa 2026'}</span>
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-white leading-tight mb-6">
            {ar.heroTitle || 'Entrenamiento de'}{' '}
            <span className="bg-gradient-to-r from-emerald-400 to-emerald-300 bg-clip-text text-transparent">
              {ar.heroTitleHighlight || 'Alto Rendimiento'}
            </span>
          </h1>
          <p className="text-lg sm:text-xl text-white/70 max-w-2xl mx-auto mb-4">
            {ar.heroSubtitle || 'Programa intensivo para cadetes y jugadores que sueñan con quedar en cadetes. Diseñado para niños de 8 a 10 años que quieren llevar su fútbol al siguiente nivel.'}
          </p>
          <p className="text-base text-white/50 max-w-xl mx-auto mb-8">
            {ar.heroLocation || '📍 Club Deportivo Palestino, Las Condes · Viernes 16:30 a 18:30'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => setCurrentPage('contact')}
              className="bg-emerald-500 hover:bg-emerald-400 text-white px-8 py-4 rounded-full text-lg font-bold transition-all hover:shadow-xl hover:shadow-emerald-500/30 hover:scale-105"
            >
              {ar.heroCTA || '¡Reserva tu cupo ahora!'}
            </button>
            <a
              href="#plan-entrenamiento"
              className="border border-white/30 hover:border-white text-white px-8 py-4 rounded-full text-lg font-medium transition-all hover:bg-white/10"
            >
              {ar.heroSecondary || 'Ver el programa'}
            </a>
          </div>

          {/* Quick stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-16 max-w-3xl mx-auto">
            {[
              { value: '2h', label: ar.stat1 || 'de entrenamiento' },
              { value: '2', label: ar.stat2 || 'entrenadores en cancha' },
              { value: '16', label: ar.stat3 || 'cupos máximo' },
              { value: '6', label: ar.stat4 || 'tests de medición' },
            ].map((s, i) => (
              <div key={i} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-4">
                <div className="text-2xl sm:text-3xl font-extrabold text-emerald-400">{s.value}</div>
                <div className="text-xs sm:text-sm text-white/60 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 4 Pilares ───────────────────────────────────────────────────────── */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900">{ar.pillarsTitle || 'Metodología integral'}</h2>
            <p className="text-gray-500 mt-3 max-w-xl mx-auto">{ar.pillarsSubtitle || 'Formamos jugadores completos con un enfoque en 4 pilares fundamentales.'}</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {pillars.map((p, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all border border-gray-100">
                <div className="text-4xl mb-4">{p.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{p.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Plan de entrenamiento (2 horas) ──────────────────────────────────── */}
      <section id="plan-entrenamiento" className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900">{ar.planTitle || 'Plan de entrenamiento'}</h2>
            <p className="text-gray-500 mt-3 max-w-2xl mx-auto">{ar.planSubtitle || '120 minutos diseñados para maximizar cada segundo en cancha. Ratio 1:8 con dos entrenadores especializados.'}</p>
          </div>
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-6 sm:left-8 top-0 bottom-0 w-0.5 bg-emerald-200 hidden sm:block" />
            <div className="space-y-4">
              {trainingBlocks.map((b, i) => (
                <div key={i} className="relative flex gap-4 sm:gap-6 group">
                  {/* Timeline dot */}
                  <div className="hidden sm:flex w-16 h-16 rounded-2xl bg-emerald-50 border-2 border-emerald-200 items-center justify-center text-2xl flex-shrink-0 z-10 group-hover:border-emerald-400 group-hover:bg-emerald-100 transition-all">
                    {b.icon}
                  </div>
                  <div className="flex-1 bg-gray-50 hover:bg-emerald-50/50 rounded-2xl p-5 sm:p-6 border border-gray-100 hover:border-emerald-200 transition-all">
                    <div className="flex flex-wrap items-center gap-3 mb-2">
                      <span className="sm:hidden text-xl">{b.icon}</span>
                      <span className="text-xs font-bold text-emerald-600 bg-emerald-100 rounded-full px-3 py-1">{b.time}</span>
                      <span className="text-xs font-medium text-gray-400">{b.min}</span>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">{b.title}</h3>
                    <p className="text-sm text-gray-500 leading-relaxed">{b.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Tests de rendimiento ────────────────────────────────────────────── */}
      <section className="py-20 bg-gradient-to-br from-gray-900 via-emerald-950 to-gray-900 text-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-extrabold">{ar.testsTitle || 'Tests de rendimiento'}</h2>
            <p className="text-white/60 mt-3 max-w-2xl mx-auto">{ar.testsSubtitle || 'Medimos el progreso real de tu hijo cada 6 semanas con una batería de 6 pruebas físicas y técnicas. Recibirás un reporte detallado con la evolución.'}</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {tests.map((te, i) => (
              <div key={i} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 hover:border-emerald-400/30 transition-all">
                <div className="text-3xl mb-3">{te.icon}</div>
                <h3 className="text-lg font-bold text-white mb-1">{te.name}</h3>
                <p className="text-sm text-white/50 leading-relaxed">{te.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <div className="inline-flex items-center gap-3 bg-emerald-500/20 border border-emerald-400/30 rounded-2xl px-6 py-4">
              <TrendingUp className="w-5 h-5 text-emerald-400" />
              <span className="text-sm text-emerald-300 font-medium">{ar.testsNote || 'Cada jugador recibirá un Informe ProKids con gráficos de evolución y recomendaciones de mejora'}</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Lo que incluye ──────────────────────────────────────────────────── */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900">{ar.includesTitle || '¿Qué incluye?'}</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-4 max-w-3xl mx-auto">
            {(ar.includesList || [
              '2 horas de entrenamiento intensivo semanal',
              '2 entrenadores especializados en cancha (ratio 1:8)',
              'Batería de 6 tests de rendimiento cada 6 semanas',
              'Informe individual con métricas y evolución',
              'Cancha de pasto sintético en Club Palestino',
              'Metodología europea de formación de base',
              'Grupos reducidos: máximo 16 jugadores',
              'Feedback personalizado cada sesión',
            ]).map((item, i) => (
              <div key={i} className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl border border-gray-100">
                <Check className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700 text-sm font-medium">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Detalles prácticos + Precio ───────────────────────────────────────── */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-10 items-start">
            {/* Info */}
            <div>
              <h2 className="text-3xl font-extrabold text-gray-900 mb-8">{ar.detailsTitle || 'Detalles del programa'}</h2>
              <div className="space-y-5">
                {[
                  { icon: <Calendar className="w-5 h-5" />, label: ar.detailDay || 'Día', value: ar.detailDayVal || 'Viernes' },
                  { icon: <Clock className="w-5 h-5" />, label: ar.detailTime || 'Horario', value: ar.detailTimeVal || '16:30 a 18:30 hrs' },
                  { icon: <MapPin className="w-5 h-5" />, label: ar.detailLocation || 'Ubicación', value: ar.detailLocationVal || 'Club Deportivo Palestino, Las Condes, Santiago' },
                  { icon: <Users className="w-5 h-5" />, label: ar.detailAge || 'Edad', value: ar.detailAgeVal || '8 a 10 años (cadetes y aspirantes)' },
                  { icon: <User className="w-5 h-5" />, label: ar.detailCoaches || 'Entrenadores', value: ar.detailCoachesVal || '2 profesores en cancha' },
                  { icon: <Target className="w-5 h-5" />, label: ar.detailCapacity || 'Cupos', value: ar.detailCapacityVal || 'Máximo 16 jugadores' },
                ].map((d, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center text-emerald-600">{d.icon}</div>
                    <div>
                      <div className="text-xs text-gray-400 uppercase tracking-wider font-medium">{d.label}</div>
                      <div className="text-gray-900 font-semibold">{d.value}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Pricing card */}
            <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-3xl p-8 text-white shadow-xl shadow-emerald-500/20">
              <div className="text-sm font-medium text-emerald-200 uppercase tracking-wider mb-2">{ar.pricingLabel || 'Inversión mensual'}</div>
              <div className="flex items-baseline gap-2 mb-1">
                <span className="text-5xl font-extrabold">$100.000</span>
                <span className="text-emerald-200 text-lg">/mes</span>
              </div>
              <p className="text-emerald-200 text-sm mb-6">{ar.pricingNote || '4 clases mensuales de 2 horas · Todo incluido'}</p>
              <div className="space-y-3 mb-8">
                {(ar.pricingFeatures || [
                  '4 clases mensuales de 120 min',
                  '2 entrenadores especializados',
                  'Tests de rendimiento cada 6 semanas',
                  'Informe individual de progreso',
                  'Cancha de pasto sintético (Palestino)',
                  'Sin matrícula · Sin compromiso anual',
                ]).map((f, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <Check className="w-4 h-4 text-emerald-300 flex-shrink-0" />
                    <span className="text-sm text-white/90">{f}</span>
                  </div>
                ))}
              </div>
              <button
                onClick={() => setCurrentPage('contact')}
                className="w-full bg-white text-emerald-700 py-4 rounded-2xl font-bold text-lg hover:bg-emerald-50 transition-all hover:shadow-lg"
              >
                {ar.pricingCTA || '¡Inscribir a mi hijo!'}
              </button>
              <p className="text-center text-emerald-200/70 text-xs mt-3">{ar.pricingDisclaimer || 'Cupos limitados · Inicio mayo 2026'}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────────────────────── */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl font-extrabold text-gray-900 text-center mb-12">{ar.faqTitle || 'Preguntas frecuentes'}</h2>
          <div className="space-y-4">
            {(ar.faqs || [
              { q: '¿Mi hijo necesita experiencia previa?', a: 'Sí, el programa es de alto rendimiento y está diseñado para cadetes o niños que aspiran a quedar en cadetes. Deben tener una base técnica previa y jugar regularmente en un club o escuela de fútbol.' },
              { q: '¿El programa prepara para entrar a cadetes?', a: 'Exactamente. El plan está diseñado para dar a los niños de 8 a 10 años las herramientas técnicas, físicas, tácticas y mentales que les permitan destacar en las pruebas de cadetes y mantener el nivel una vez adentro.' },
              { q: '¿Qué pasa si llueve o hay mal clima?', a: 'La cancha de Palestino es de pasto sintético, por lo que se juega en la mayoría de condiciones. En caso de suspensión, se reprograma la sesión.' },
              { q: '¿Cada cuánto son los tests de rendimiento?', a: 'Cada 6 semanas realizamos la batería completa de tests. Recibirás un informe con gráficos de evolución y recomendaciones personalizadas.' },
              { q: '¿Puedo inscribirlo en cualquier momento?', a: 'Sí, puedes sumarte en cualquier momento siempre que haya cupos disponibles. El programa está diseñado para integrar nuevos jugadores.' },
              { q: '¿Qué debe traer mi hijo?', a: 'Ropa deportiva cómoda, canilleras, zapatillas de fútbol (ojalá para pasto sintético), botella de agua y bloqueador solar.' },
            ]).map((faq, i) => (
              <details key={i} className="group bg-gray-50 rounded-2xl border border-gray-100 overflow-hidden">
                <summary className="flex items-center justify-between p-5 cursor-pointer hover:bg-gray-100 transition-colors">
                  <span className="font-semibold text-gray-900 text-sm sm:text-base pr-4">{faq.q}</span>
                  <ChevronRight className="w-5 h-5 text-gray-400 group-open:rotate-90 transition-transform flex-shrink-0" />
                </summary>
                <div className="px-5 pb-5 text-sm text-gray-500 leading-relaxed">{faq.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA final ───────────────────────────────────────────────────────── */}
      <section className="py-20 bg-gradient-to-br from-emerald-600 to-emerald-700">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-4">{ar.ctaTitle || '¿Listo para el siguiente nivel?'}</h2>
          <p className="text-emerald-100 text-lg mb-8 max-w-xl mx-auto">{ar.ctaSubtitle || 'Los cupos son limitados a 16 jugadores para asegurar atención personalizada. Reserva el lugar de tu hijo hoy.'}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => setCurrentPage('contact')}
              className="bg-white text-emerald-700 px-8 py-4 rounded-full text-lg font-bold hover:bg-emerald-50 transition-all hover:shadow-xl hover:scale-105"
            >
              {ar.ctaCTA || '¡Reservar cupo!'}
            </button>
            <a
              href="https://wa.me/17869564198?text=Hola%2C%20quiero%20información%20sobre%20el%20programa%20de%20Alto%20Rendimiento"
              target="_blank"
              rel="noopener noreferrer"
              className="border-2 border-white/30 text-white px-8 py-4 rounded-full text-lg font-medium hover:bg-white/10 transition-all flex items-center justify-center gap-2"
            >
              <MessageCircle className="w-5 h-5" />
              {ar.ctaWhatsapp || 'Consultar por WhatsApp'}
            </a>
          </div>
        </div>
      </section>

      {/* Footer minimal */}
      <footer className="bg-gray-900 py-8 text-center">
        <p className="text-gray-500 text-sm">© 2026 ProKids Talent. {ar.footerText || 'Todos los derechos reservados.'}</p>
      </footer>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// Academy Page — full flyer reproduction, neon high-performance aesthetic
// ════════════════════════════════════════════════════════════════════════════
const AcademyPage = ({ setCurrentPage }) => {
  const { t } = useLanguage();
  const ac = t.academy || {};

  // WhatsApp link for the academy.
  const waLink = 'https://wa.me/56955110639?text=Hola%2C%20quiero%20inscribir%20a%20mi%20hijo%20en%20ProKids%20Talent%20Academy';
  const phoneLabel = '+56 9 5511 0639';

  // El alto rendimiento es un proceso — 4 pilares
  const processPillars = [
    { icon: <Calendar className="w-7 h-7" />, title: ac.process1 || 'Entrenamiento Constante' },
    { icon: <TrendingUp className="w-7 h-7" />, title: ac.process2 || 'Mejoras Progresivas' },
    { icon: <Brain className="w-7 h-7" />, title: ac.process3 || 'Mentalidad Ganadora' },
    { icon: <Target className="w-7 h-7" />, title: ac.process4 || 'Resultados Reales' },
  ];

  // Línea de tiempo del proceso
  const journey = [
    { phase: ac.journey1 || '1º Mes', label: ac.journey1Label || 'Adaptación', icon: '🌱' },
    { phase: ac.journey2 || '3º Mes', label: ac.journey2Label || 'Mejoras', icon: '📈' },
    { phase: ac.journey3 || '6º Mes', label: ac.journey3Label || 'Confianza', icon: '🔥' },
    { phase: ac.journey4 || '12+ Meses', label: ac.journey4Label || 'Resultados', icon: '🏆' },
  ];

  // Beneficios
  const benefits = [
    { icon: <Users className="w-6 h-6" />, title: ac.benefit1 || 'Entrenamientos adaptados a su edad' },
    { icon: <Dumbbell className="w-6 h-6" />, title: ac.benefit2 || 'Desarrollo físico, técnico y táctico' },
    { icon: <Brain className="w-6 h-6" />, title: ac.benefit3 || 'Mentalidad y disciplina' },
    { icon: <Star className="w-6 h-6" />, title: ac.benefit4 || 'Ambiente positivo y motivador' },
  ];

  // Valores de la academia
  const values = [
    { icon: <Trophy className="w-5 h-5" />, label: ac.value1 || 'Disciplina' },
    { icon: <Dumbbell className="w-5 h-5" />, label: ac.value2 || 'Esfuerzo' },
    { icon: <Target className="w-5 h-5" />, label: ac.value3 || 'Enfoque' },
    { icon: <Users className="w-5 h-5" />, label: ac.value4 || 'Trabajo en Equipo' },
    { icon: <TrendingUp className="w-5 h-5" />, label: ac.value5 || 'Resultados' },
  ];

  // Clubes colaboradores (monogramas estilizados, no logos oficiales)
  const clubs = [
    { name: 'U de Chile', monogram: 'U', from: 'from-blue-500', to: 'to-blue-700', ring: 'ring-blue-400/40' },
    { name: 'Unión Española', monogram: 'UE', from: 'from-red-500', to: 'to-red-700', ring: 'ring-red-400/40' },
  ];

  // Videos de entrenamiento
  const videos = [
    { title: ac.video1 || 'Entrenamiento técnico', tag: ac.videoTag1 || 'Técnica', img: '/academy/video-tecnica.jpg' },
    { title: ac.video2 || 'Colaboración con clubes', tag: ac.videoTag2 || 'Colaboración', img: '/academy/video-colaboracion.jpg' },
    { title: ac.video3 || 'Contenido exclusivo', tag: ac.videoTag3 || 'Exclusivo', img: '/academy/video-exclusivo.jpg' },
  ];

  // Calendario de clases 2026
  const calendar = [
    { month: 'Mayo', dates: ['15/05', '22/05', '29/05'], classes: ['1', '2', '3'] },
    { month: 'Junio', dates: ['05/06', '12/06', '19/06', '26/06'], classes: ['4', '1', '2', '3'] },
    { month: 'Julio', dates: ['03/07', '10/07', '17/07', '24/07', '31/07'], classes: ['4', '1', '2', '3', '4'] },
    { month: 'Agosto', dates: ['07/08', '14/08', '21/08', '28/08'], classes: ['1', '2', '3', '4'] },
    { month: 'Septiembre', dates: ['04/09', '11/09', '18/09', '25/09'], classes: ['1', '2', '3', '4'] },
    { month: 'Octubre', dates: ['02/10', '09/10', '16/10', '23/10', '30/10'], classes: ['1', '2', '3', '4', '1'] },
    { month: 'Noviembre', dates: ['06/11', '13/11', '20/11', '27/11'], classes: ['2', '3', '4', '1'] },
    { month: 'Diciembre', dates: ['04/12', '11/12', '18/12', '25/12'], classes: ['2', '3', '4', '–'] },
  ];

  // Valores 2026 — planes
  const plans = [
    {
      name: ac.planMonthly || 'Mensual', detail: ac.planMonthlyDetail || '4 clases',
      normal: '$100.000', discount: null, final: '$100.000', perClass: '$25.000',
      highlight: false, ribbon: null,
    },
    {
      name: ac.planQuarterly || 'Trimestral', detail: ac.planQuarterlyDetail || '12 clases',
      normal: '$300.000', discount: '20%', final: '$240.000', perClass: '$20.000',
      highlight: false, ribbon: ac.popular || 'Popular',
    },
    {
      name: ac.planBiannual || 'Semestral', detail: ac.planBiannualDetail || '24 clases',
      normal: '$600.000', discount: '33%', final: '$400.000', perClass: '$16.667 aprox.',
      highlight: true, ribbon: ac.bestValue || 'Mejor valor',
    },
  ];

  const SectionBadge = ({ children }) => (
    <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-400/30 rounded-full px-4 py-1.5 mb-5">
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
      <span className="text-emerald-300 font-semibold text-xs tracking-[0.2em] uppercase">{children}</span>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#070b0a] text-white selection:bg-emerald-500/30">
      {/* ── HERO ─────────────────────────────────────────────────────────────── */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background photo */}
        <div className="absolute inset-0">
          <img src="/academy/hero-training.jpg" alt="" aria-hidden="true" className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0 bg-[#070b0a]/75" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#070b0a] via-transparent to-[#070b0a]" />
        </div>
        {/* Glow blobs */}
        <div className="absolute inset-0">
          <div className="absolute -top-20 -left-20 w-[28rem] h-[28rem] bg-emerald-500 rounded-full blur-[160px] animate-glow" />
          <div className="absolute bottom-0 right-0 w-[32rem] h-[32rem] bg-lime-500 rounded-full blur-[180px] animate-glow" style={{ animationDelay: '2s' }} />
          <div className="absolute top-1/3 right-1/4 w-72 h-72 bg-cyan-500 rounded-full blur-[140px] animate-glow opacity-40" style={{ animationDelay: '4s' }} />
        </div>
        {/* Field grid overlay */}
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)',
            backgroundSize: '54px 54px',
            maskImage: 'radial-gradient(ellipse 70% 60% at 50% 40%, black 40%, transparent 100%)',
            WebkitMaskImage: 'radial-gradient(ellipse 70% 60% at 50% 40%, black 40%, transparent 100%)',
          }}
        />

        <div className="relative z-10 max-w-5xl mx-auto px-4 text-center pt-28 pb-20">
          <div className="inline-flex items-center gap-2 bg-white/5 backdrop-blur-sm border border-emerald-400/30 rounded-full px-5 py-2 mb-8 animate-rise">
            <Star className="w-4 h-4 text-lime-400 fill-lime-400" />
            <span className="text-white/90 text-sm font-semibold tracking-wide uppercase">
              {ac.heroBadge || 'Academia de Alto Rendimiento'}
            </span>
          </div>

          {/* Wordmark */}
          <h1 className="text-5xl sm:text-6xl md:text-8xl font-extrabold leading-[0.95] mb-4 animate-rise" style={{ animationDelay: '0.05s' }}>
            <span className="text-white">ProKids </span>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 via-lime-300 to-emerald-400 animate-shimmer text-neon">
              Talent
            </span>
          </h1>
          <p className="text-base sm:text-lg text-lime-300/90 font-semibold tracking-[0.25em] uppercase mb-7 animate-rise" style={{ animationDelay: '0.1s' }}>
            {ac.heroTagline || 'Descubrimos talento, impulsamos sueños'}
          </p>

          <p className="text-lg sm:text-2xl text-white/70 max-w-2xl mx-auto mb-3 leading-relaxed animate-rise" style={{ animationDelay: '0.15s' }}>
            {ac.heroLine1 || 'El alto rendimiento es un'}{' '}
            <span className="text-emerald-400 font-bold">{ac.heroLine1Highlight || 'proceso'}</span>.
          </p>
          <p className="text-base sm:text-lg text-white/50 max-w-xl mx-auto mb-10 animate-rise" style={{ animationDelay: '0.2s' }}>
            {ac.heroSubtitle || 'Los cambios reales requieren tiempo, disciplina y continuidad durante varios meses. La constancia hace la diferencia.'}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-rise" style={{ animationDelay: '0.25s' }}>
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-gradient-to-r from-emerald-500 to-lime-500 text-gray-900 px-8 py-4 rounded-full text-lg font-extrabold transition-all hover:shadow-[0_0_40px_-5px_rgba(132,204,22,0.6)] hover:scale-105 flex items-center justify-center gap-2"
            >
              {ac.heroCTA || '¡Inscríbete ahora!'}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="#academy-calendario"
              className="border border-white/20 hover:border-emerald-400/60 text-white px-8 py-4 rounded-full text-lg font-semibold transition-all hover:bg-white/5 flex items-center justify-center gap-2"
            >
              <Calendar className="w-5 h-5" />
              {ac.heroSecondary || 'Ver calendario y valores'}
            </a>
          </div>

          {/* Quick chips */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mt-16 max-w-3xl mx-auto animate-rise" style={{ animationDelay: '0.3s' }}>
            {[
              { value: '2h', label: ac.chip1 || 'por sesión' },
              { value: '4', label: ac.chip2 || 'clases al mes' },
              { value: '2', label: ac.chip3 || 'clubes aliados' },
              { value: '33%', label: ac.chip4 || 'dcto. semestral' },
            ].map((s, i) => (
              <div key={i} className="bg-white/[0.04] backdrop-blur-sm border border-white/10 rounded-2xl p-4 hover:border-emerald-400/40 transition-all">
                <div className="text-2xl sm:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-lime-300">{s.value}</div>
                <div className="text-xs sm:text-sm text-white/50 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* scroll cue */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-emerald-400/40 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-emerald-400/70 rounded-full" />
          </div>
        </div>
      </section>

      {/* ── EL ALTO RENDIMIENTO ES UN PROCESO ────────────────────────────────── */}
      <section className="relative py-24 border-t border-white/5">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <SectionBadge>{ac.processBadge || 'Nuestra filosofía'}</SectionBadge>
            <h2 className="text-3xl sm:text-5xl font-extrabold mb-4">
              {ac.processTitle || 'El alto rendimiento es un'}{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-lime-300">{ac.processTitleHighlight || 'proceso'}</span>
            </h2>
            <p className="text-white/50 max-w-2xl mx-auto text-lg">
              {ac.processSubtitle || 'Los cambios reales requieren tiempo, disciplina y continuidad durante varios meses.'}
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {processPillars.map((p, i) => (
              <div
                key={i}
                className="group relative bg-white/[0.03] border border-white/10 rounded-3xl p-6 sm:p-8 text-center hover:border-emerald-400/40 hover:bg-white/[0.06] transition-all duration-300 hover:shadow-[0_0_40px_-10px_rgba(52,211,153,0.4)]"
              >
                <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-emerald-500/20 to-lime-500/10 border border-emerald-400/30 flex items-center justify-center text-emerald-400 mb-5 group-hover:scale-110 group-hover:text-lime-300 transition-all">
                  {p.icon}
                </div>
                <h3 className="font-bold text-base sm:text-lg leading-tight">{p.title}</h3>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <span className="inline-block text-xl sm:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 via-lime-300 to-emerald-400 animate-shimmer">
              {ac.processQuote || 'La constancia hace la diferencia.'}
            </span>
          </div>
        </div>
      </section>

      {/* ── LÍNEA DE TIEMPO DEL PROCESO ──────────────────────────────────────── */}
      <section className="relative py-24 bg-gradient-to-b from-transparent via-emerald-950/20 to-transparent">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <SectionBadge>{ac.journeyBadge || 'La evolución'}</SectionBadge>
            <h2 className="text-3xl sm:text-5xl font-extrabold mb-4">{ac.journeyTitle || 'Un camino de varios meses'}</h2>
            <p className="text-white/50 max-w-2xl mx-auto text-lg">{ac.journeySubtitle || 'Para ver cambios reales y duraderos en tu juego.'}</p>
          </div>

          <div className="relative">
            {/* connecting gradient bar */}
            <div className="hidden lg:block absolute top-[3.25rem] left-[12%] right-[12%] h-1 bg-gradient-to-r from-emerald-500/30 via-lime-400/60 to-emerald-400 rounded-full" />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {journey.map((j, i) => (
                <div key={i} className="relative flex flex-col items-center text-center">
                  <div className="relative z-10 w-[6.5rem] h-[6.5rem] rounded-full bg-[#0b1110] border-2 border-emerald-400/40 flex items-center justify-center text-4xl mb-5 shadow-[0_0_30px_-8px_rgba(52,211,153,0.6)]">
                    {j.icon}
                    <span className="absolute -top-1 -right-1 w-7 h-7 rounded-full bg-gradient-to-br from-emerald-400 to-lime-400 text-gray-900 text-xs font-extrabold flex items-center justify-center">
                      {i + 1}
                    </span>
                  </div>
                  <div className="text-emerald-400 font-extrabold text-lg">{j.phase}</div>
                  <div className="text-white/60 text-sm mt-1">{j.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── BENEFICIOS ───────────────────────────────────────────────────────── */}
      <section className="relative py-24 border-t border-white/5">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-16">
            <SectionBadge>{ac.benefitsBadge || 'Beneficios'}</SectionBadge>
            <h2 className="text-3xl sm:text-5xl font-extrabold">{ac.benefitsTitle || 'Lo que vive cada jugador'}</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            {benefits.map((b, i) => (
              <div
                key={i}
                className="group flex items-center gap-5 bg-white/[0.03] border border-white/10 rounded-2xl p-6 hover:border-emerald-400/40 hover:bg-white/[0.06] transition-all"
              >
                <div className="w-14 h-14 flex-shrink-0 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-lime-500/10 border border-emerald-400/30 flex items-center justify-center text-emerald-400 group-hover:scale-110 transition-transform">
                  {b.icon}
                </div>
                <span className="font-semibold text-lg">{b.title}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── VALORES (chips) ──────────────────────────────────────────────────── */}
      <section className="relative py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
            {values.map((v, i) => (
              <div
                key={i}
                className="flex items-center gap-2.5 bg-white/[0.04] border border-white/10 rounded-full pl-4 pr-5 py-3 hover:border-lime-400/50 hover:text-lime-300 transition-all"
              >
                <span className="text-emerald-400">{v.icon}</span>
                <span className="font-bold text-sm tracking-wide uppercase">{v.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── VIDEOS DE ENTRENAMIENTO ──────────────────────────────────────────── */}
      <section className="relative py-24 border-t border-white/5">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <SectionBadge>{ac.videosBadge || 'Contenido'}</SectionBadge>
            <h2 className="text-3xl sm:text-5xl font-extrabold mb-4">{ac.videosTitle || 'Videos de entrenamiento'}</h2>
            <p className="text-white/50 max-w-2xl mx-auto text-lg">
              {ac.videosSubtitle || 'Mira nuestros entrenamientos, colaboraciones y contenido exclusivo.'}
            </p>
          </div>
          <div className="grid sm:grid-cols-3 gap-5">
            {videos.map((v, i) => (
              <div key={i} className="group cursor-pointer">
                <div className="relative aspect-video rounded-2xl overflow-hidden border border-white/10 group-hover:border-emerald-400/40 transition-all">
                  <img
                    src={v.img}
                    alt={v.title}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-500 to-lime-500 flex items-center justify-center shadow-[0_0_30px_-5px_rgba(132,204,22,0.7)] opacity-80 group-hover:opacity-100 group-hover:scale-110 transition-all">
                      <Play className="w-7 h-7 text-gray-900 ml-1" fill="currentColor" />
                    </div>
                  </div>
                  <span className="absolute top-3 left-3 text-xs font-bold text-emerald-300 bg-black/40 backdrop-blur-sm border border-emerald-400/30 rounded-full px-3 py-1">
                    {v.tag}
                  </span>
                </div>
                <h3 className="mt-3 font-semibold text-white/90">{v.title}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── COLABORACIONES ───────────────────────────────────────────────────── */}
      <section className="relative py-24 bg-gradient-to-b from-transparent via-emerald-950/20 to-transparent">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <SectionBadge>{ac.clubsBadge || 'Colaboraciones'}</SectionBadge>
          <h2 className="text-3xl sm:text-5xl font-extrabold mb-4">{ac.clubsTitle || 'Trabajamos junto a clubes profesionales'}</h2>
          <p className="text-white/50 max-w-2xl mx-auto text-lg mb-14">
            {ac.clubsSubtitle || 'Para abrir oportunidades reales a nuestros jugadores.'}
          </p>
          <div className="flex flex-wrap justify-center gap-6 sm:gap-10">
            {clubs.map((c, i) => (
              <div
                key={i}
                className="group flex flex-col items-center gap-4 bg-white/[0.03] border border-white/10 rounded-3xl px-10 py-8 hover:border-emerald-400/40 hover:bg-white/[0.06] transition-all"
              >
                <div className={`w-24 h-24 rounded-full bg-gradient-to-br ${c.from} ${c.to} ring-4 ${c.ring} flex items-center justify-center text-3xl font-extrabold text-white shadow-xl group-hover:scale-105 transition-transform`}>
                  {c.monogram}
                </div>
                <span className="font-bold text-lg">{c.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CALENDARIO DE CLASES 2026 ────────────────────────────────────────── */}
      <section id="academy-calendario" className="relative py-24 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-14">
            <SectionBadge>{ac.calendarBadge || 'Planificación'}</SectionBadge>
            <h2 className="text-3xl sm:text-5xl font-extrabold mb-3">
              {ac.calendarTitle || 'Calendario de clases'} <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-lime-300">2026</span>
            </h2>
            <p className="text-white/50 text-lg">{ac.calendarSubtitle || 'Todas las clases se realizan los días viernes.'}</p>
          </div>

          <div className="bg-white/[0.03] border border-white/10 rounded-3xl overflow-hidden shadow-2xl">
            <div className="overflow-x-auto">
              <table className="w-full text-left min-w-[520px]">
                <thead>
                  <tr className="bg-gradient-to-r from-emerald-500/15 to-lime-500/10 border-b border-white/10">
                    <th className="py-4 px-5 text-xs font-bold tracking-wider uppercase text-emerald-300">{ac.colMonth || 'Mes'}</th>
                    <th className="py-4 px-5 text-xs font-bold tracking-wider uppercase text-emerald-300">{ac.colDates || 'Fechas (viernes)'}</th>
                    <th className="py-4 px-5 text-xs font-bold tracking-wider uppercase text-emerald-300 text-right">{ac.colClasses || 'Clases'}</th>
                  </tr>
                </thead>
                <tbody>
                  {calendar.map((row, i) => (
                    <tr key={i} className={`border-b border-white/5 last:border-0 transition-colors hover:bg-emerald-500/5 ${i % 2 ? 'bg-white/[0.015]' : ''}`}>
                      <td className="py-4 px-5 font-bold text-white whitespace-nowrap">{row.month}</td>
                      <td className="py-4 px-5">
                        <div className="flex flex-wrap gap-1.5">
                          {row.dates.map((d, k) => (
                            <span key={k} className="text-xs text-white/70 bg-white/5 border border-white/10 rounded-md px-2 py-1">{d}</span>
                          ))}
                        </div>
                      </td>
                      <td className="py-4 px-5">
                        <div className="flex flex-wrap gap-1.5 justify-end">
                          {row.classes.map((c, k) => (
                            <span
                              key={k}
                              className={`w-7 h-7 flex items-center justify-center text-xs font-extrabold rounded-lg ${
                                c === '–'
                                  ? 'text-white/30 bg-white/5'
                                  : 'text-gray-900 bg-gradient-to-br from-emerald-400 to-lime-400'
                              }`}
                            >
                              {c}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* ── VALORES 2026 (precios) ───────────────────────────────────────────── */}
      <section id="academy-valores" className="relative py-24 bg-gradient-to-b from-transparent via-emerald-950/20 to-transparent">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <SectionBadge>{ac.pricingBadge || 'Inversión'}</SectionBadge>
            <h2 className="text-3xl sm:text-5xl font-extrabold mb-3">
              {ac.pricingTitle || 'Valores'} <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-lime-300">2026</span>
            </h2>
            <p className="text-white/50 text-lg">{ac.pricingSubtitle || 'Mientras más constancia, mayor el descuento.'}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 items-stretch">
            {plans.map((p, i) => (
              <div
                key={i}
                className={`relative rounded-3xl p-8 flex flex-col transition-all ${
                  p.highlight
                    ? 'bg-gradient-to-b from-emerald-500/15 to-lime-500/5 border-2 border-emerald-400/60 shadow-[0_0_50px_-12px_rgba(52,211,153,0.5)] md:scale-105'
                    : 'bg-white/[0.03] border border-white/10 hover:border-emerald-400/30'
                }`}
              >
                {p.ribbon && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                    <span className={`px-4 py-1 rounded-full text-xs font-extrabold whitespace-nowrap ${
                      p.highlight ? 'bg-gradient-to-r from-emerald-400 to-lime-400 text-gray-900' : 'bg-white/10 border border-white/20 text-white/80'
                    }`}>
                      {p.ribbon}
                    </span>
                  </div>
                )}

                <div className="flex items-center justify-between mb-1">
                  <h3 className="text-xl font-extrabold">{p.name}</h3>
                  {p.discount && (
                    <span className="bg-red-500 text-white text-xs font-extrabold px-2.5 py-1 rounded-lg">-{p.discount}</span>
                  )}
                </div>
                <p className="text-white/50 text-sm mb-6">{p.detail}</p>

                <div className="mb-1 flex items-baseline gap-2">
                  <span className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-lime-300">{p.final}</span>
                  {p.discount && <span className="text-white/40 line-through text-sm">{p.normal}</span>}
                </div>
                <p className="text-white/50 text-sm mb-8">{p.perClass} {ac.perClass || 'por clase'}</p>

                <a
                  href={waLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`mt-auto w-full text-center py-3.5 rounded-2xl font-bold transition-all ${
                    p.highlight
                      ? 'bg-gradient-to-r from-emerald-500 to-lime-500 text-gray-900 hover:shadow-[0_0_30px_-5px_rgba(132,204,22,0.6)]'
                      : 'bg-white/5 border border-white/15 text-white hover:bg-white/10 hover:border-emerald-400/40'
                  }`}
                >
                  {ac.choosePlan || 'Elegir plan'}
                </a>
              </div>
            ))}
          </div>

          <div className="mt-10 flex items-center justify-center gap-2.5 text-center">
            <Star className="w-4 h-4 text-lime-400 fill-lime-400 flex-shrink-0" />
            <p className="text-white/60 text-sm">
              {ac.pricingNote || 'Descuento por pago anticipado en planes trimestral y semestral.'}
            </p>
          </div>
        </div>
      </section>

      {/* ── CTA FINAL + CONTACTO ─────────────────────────────────────────────── */}
      <section className="relative py-28 overflow-hidden border-t border-white/5">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-emerald-500 rounded-full blur-[160px] animate-glow opacity-30" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-lime-500 rounded-full blur-[160px] animate-glow opacity-30" style={{ animationDelay: '3s' }} />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-4xl sm:text-6xl font-extrabold mb-4 leading-tight">
            {ac.ctaTitle1 || 'Sé parte de'}{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 via-lime-300 to-emerald-400 animate-shimmer text-neon">ProKids Talent</span>
          </h2>
          <p className="text-xl text-white/70 mb-10">
            {ac.ctaSubtitle || '¡El lugar donde tu talento llega más lejos!'}
          </p>

          <a
            href={waLink}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-3 bg-gradient-to-r from-emerald-500 to-lime-500 text-gray-900 px-10 py-5 rounded-full text-xl font-extrabold transition-all hover:shadow-[0_0_50px_-8px_rgba(132,204,22,0.7)] hover:scale-105 mb-12"
          >
            <MessageCircle className="w-6 h-6" />
            {ac.ctaButton || '¡Inscríbete ahora!'}
            <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
          </a>

          {/* Contacto */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 text-white/70">
            <a href="https://instagram.com/prokidstalent_academy" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-lime-300 transition-colors">
              <Instagram className="w-5 h-5" />
              <span className="font-medium">@prokidstalent_academy</span>
            </a>
            <span className="hidden sm:block w-px h-5 bg-white/15" />
            <a href="https://www.prokidstalent.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-lime-300 transition-colors">
              <Globe className="w-5 h-5" />
              <span className="font-medium">www.prokidstalent.com</span>
            </a>
            <span className="hidden sm:block w-px h-5 bg-white/15" />
            <a href={waLink} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-lime-300 transition-colors">
              <Phone className="w-5 h-5" />
              <span className="font-medium">{phoneLabel}</span>
            </a>
          </div>
        </div>
      </section>

      {/* Footer minimal */}
      <footer className="bg-black/40 py-8 text-center border-t border-white/5">
        <p className="text-white/40 text-sm">© 2026 ProKids Talent. {ac.footerText || 'Todos los derechos reservados.'}</p>
      </footer>
    </div>
  );
};

// Inner App Component (needs language context)
function AppContent() {
  const [currentPage, setCurrentPage] = useState('home');
  const [scrolled, setScrolled] = useState(false);
  const [adminUser, setAdminUser] = useState(null);
  const [adminLoginError, setAdminLoginError] = useState('');
  const [adminLoginForm, setAdminLoginForm] = useState({ email: '', password: '' });
  const [adminLogging, setAdminLogging] = useState(false);

  // Detect /admin URL
  useEffect(() => {
    if (window.location.pathname === '/admin') {
      setCurrentPage('admin');
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => { setScrolled(window.scrollY > 50); };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ── Admin route ───────────────────────────────────────────────────────────
  if (currentPage === 'admin') {
    if (adminUser) {
      return (
        <AdminDashboard
          onLogout={() => {
            clearTokens();
            setAdminUser(null);
            setCurrentPage('home');
            window.history.pushState({}, '', '/');
          }}
        />
      );
    }

    // Admin login form
    const handleAdminLogin = async (e) => {
      e.preventDefault();
      setAdminLogging(true);
      setAdminLoginError('');
      try {
        const data = await authApi.login(adminLoginForm.email, adminLoginForm.password);
        if (data.user?.role !== 'ADMIN') throw new Error('Sin acceso de administrador');
        localStorage.setItem('prokids_token', data.token);
        if (data.refreshToken) localStorage.setItem('prokids_refresh_token', data.refreshToken);
        setAdminUser(data.user);
      } catch (err) {
        setAdminLoginError(err.message || 'Credenciales incorrectas');
      } finally {
        setAdminLogging(false);
      }
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-emerald-900 to-gray-900 flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-emerald-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-xl">
              <Trophy className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white">ProKids Admin</h1>
            <p className="text-white/60 text-sm mt-1">Panel de administración</p>
          </div>

          <form onSubmit={handleAdminLogin} className="bg-white rounded-3xl shadow-2xl p-8 space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <input
                type="email"
                value={adminLoginForm.email}
                onChange={e => setAdminLoginForm(f => ({...f, email: e.target.value}))}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="admin@prokidstalent.com"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Contraseña</label>
              <input
                type="password"
                value={adminLoginForm.password}
                onChange={e => setAdminLoginForm(f => ({...f, password: e.target.value}))}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="••••••••"
                required
              />
            </div>
            {adminLoginError && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-600">
                {adminLoginError}
              </div>
            )}
            <button
              type="submit"
              disabled={adminLogging}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3.5 rounded-xl font-semibold transition disabled:opacity-50"
            >
              {adminLogging ? 'Ingresando…' : 'Ingresar al panel'}
            </button>
            <button
              type="button"
              onClick={() => { setCurrentPage('home'); window.history.pushState({}, '', '/'); }}
              className="w-full text-center text-sm text-gray-400 hover:text-gray-600 transition"
            >
              ← Volver a la web
            </button>
          </form>
        </div>
      </div>
    );
  }


  if (currentPage === 'alto-rendimiento') {
    return (
      <>
        <Navigation currentPage={currentPage} setCurrentPage={setCurrentPage} scrolled={true} />
        <AltoRendimientoPage setCurrentPage={setCurrentPage} />
        <WhatsAppButton />
      </>
    );
  }

  if (currentPage === 'academy') {
    return (
      <>
        <Navigation currentPage={currentPage} setCurrentPage={setCurrentPage} scrolled={true} />
        <AcademyPage setCurrentPage={setCurrentPage} />
        <WhatsAppButton />
      </>
    );
  }

  if (currentPage === 'player') {
    return (
      <>
        <Navigation currentPage={currentPage} setCurrentPage={setCurrentPage} scrolled={true} />
        <PlayerProfilePage setCurrentPage={setCurrentPage} />
        <WhatsAppButton />
      </>
    );
  }

  if (currentPage === 'register') {
    return (
      <>
        <Navigation currentPage={currentPage} setCurrentPage={setCurrentPage} scrolled={true} />
        <PlayerRegistrationPage setCurrentPage={setCurrentPage} />
        <WhatsAppButton />
      </>
    );
  }

  if (currentPage === 'contact') {
    return (
      <>
        <Navigation currentPage={currentPage} setCurrentPage={setCurrentPage} scrolled={true} />
        <ContactPage setCurrentPage={setCurrentPage} />
        <WhatsAppButton />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navigation currentPage={currentPage} setCurrentPage={setCurrentPage} scrolled={scrolled} />
      <HeroSection />
      <PillarsSection />
      <IncludedSection />
      <ELearningSection />
      <PricingSection />
      <TestimonialsSection />
      <SponsorsSection />
      <FAQSection />
      <CTASection />
      <Footer />
      <WhatsAppButton />
    </div>
  );
}

// Main App Component with Language Provider
function App() {
  const [language, setLanguage] = useState('es');
  const t = translations[language];

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      <AppContent />
    </LanguageContext.Provider>
  );
}

export default App;
